// cSpell:ignore aandeel eigenaar Eigenaarsaandeel forfait Forfait forfait's gedeeld gemeen hertest huurder huurleegstand Keuze kosten overschot pand pcts privé Provisies Samenvatting sleutel stappenplan Tuinonderhoud unticked verdeel verdeelsleutel Verdeelsleutel Verschil vertreksafrekening volgens
/* eslint-disable no-nested-ternary */
// Pure-function calculation engine. Spec §9.
// No side effects, no IO. Easy to unit-test.

import { withBilledTarget } from "./chargeTarget.utils";
import type { IProvisionLine } from "./contractProvision.utils";
import { costPartyKey, splitVatOverParties } from "./costVat.utils";
import {
  correctionDaysOf,
  correctionInvoiceIds,
  departureCorrectionKey,
  IDepartureCorrection,
  IDepartureCorrectionInvoice,
  ISettledLeaseRow,
  leaseCorrectionId,
} from "./departureCorrection.utils";
import { daysWithin, leaseFixedFeePeriods } from "./fixedFeePeriods.utils";
import { existedSince } from "./groupUnits.utils";
import {
  fromCents,
  splitCents,
  tenantShareCents,
  toCents,
  vatCents,
} from "./money.utils";
import { sharedDayWeights } from "./occupancy.utils";
import {
  ExpectedProvision,
  expectedProvision,
  ProvisionSlice,
} from "./provisionHistory";
import {
  addedProvisionLineKey,
  IProvisionCoverage,
  isLeaseOptedOut,
  isProvisionLineIncluded,
  linesStartTicked,
  WHOLE_LEASE_KEY,
} from "./provisionLineInclusion.utils";
import {
  IProvisionLineRow,
  provisionLineRows,
} from "./provisionLineRows.utils";
import {
  addedProvisionKindOf,
  buildKinds,
  COMMON_KIND,
  PRIVATE_KIND,
  costKindOf,
  fixedFeeKindsOf,
  isFixedFeeKindKey,
  ISettlementKind,
  ISettlementKindProvision,
  isExtraKind,
  kindsAddUp,
  provisionLineKindOf,
  sliceOfFixedFeeKind,
  TFixedFeeKindKey,
  TSettlementKindKey,
} from "./settlementKinds.utils";
import {
  settlementEligible,
  ApportionmentKey,
  ContractRef,
  DistributionMethod,
  IAlreadySettledLine,
  IExtraProvision,
  ISettledDeparture,
  Invoice,
  isOwnerOnlyCategory,
  resolveDistributionMethod,
  PerContractResult,
  SettlementDraftState,
  settlementAmount,
  vatPercent,
} from "./types";

const MS_PER_DAY = 86_400_000;

// A contract listed twice (two pages of a paged contract list overlapped)
// must never be billed twice, and must not halve its own share with itself:
// every contract settles once, under its own id.
export const uniqueContracts = (contracts: ContractRef[]): ContractRef[] => {
  const seen = new Set<string>();
  return contracts.filter(c => {
    if (seen.has(c.id)) return false;
    seen.add(c.id);
    return true;
  });
};

function parseDate(iso: string): Date {
  return new Date(`${iso.slice(0, 10)}T00:00:00Z`);
}

export function overlapDays(
  aFrom: string,
  aTo: string,
  bFrom: string,
  bTo: string,
): number {
  const start = Math.max(
    parseDate(aFrom).getTime(),
    parseDate(bFrom).getTime(),
  );
  const end = Math.min(parseDate(aTo).getTime(), parseDate(bTo).getTime());
  if (end < start) return 0;
  return Math.round((end - start) / MS_PER_DAY) + 1;
}

export function daysInPeriod(from: string, to: string): number {
  const span = parseDate(to).getTime() - parseDate(from).getTime();
  if (span < 0) return 0;
  return Math.round(span / MS_PER_DAY) + 1;
}

// Pro-rata months between two inclusive ISO dates, counted PER CALENDAR
// MONTH: a full calendar month always counts as exactly 1 (February
// included), a partial month as its share of that month's own length.
//
// The old days/30.44 approximation (rounded to 1 decimal) mis-billed every
// provision: a full February counted as 0.9 months, Jan–Jun as 5.9, and two
// sequential tenants covering one year together came to 11.9 months instead
// of 12 — a gap no broker can explain to a tenant.
export function monthsBetween(from: string, to: string): number {
  const start = parseDate(from);
  const end = parseDate(to);
  if (end < start) return 0;

  let months = 0;
  let year = start.getUTCFullYear();
  let month = start.getUTCMonth();

  while (
    year < end.getUTCFullYear() ||
    (year === end.getUTCFullYear() && month <= end.getUTCMonth())
  ) {
    const monthStart = new Date(Date.UTC(year, month, 1));
    const monthEnd = new Date(Date.UTC(year, month + 1, 0));
    const daysInMonth = monthEnd.getUTCDate();
    const covFrom = monthStart < start ? start : monthStart;
    const covTo = monthEnd > end ? end : monthEnd;
    const covered =
      Math.round((covTo.getTime() - covFrom.getTime()) / MS_PER_DAY) + 1;
    months += covered / daysInMonth;
    month += 1;
    if (month > 11) {
      month = 0;
      year += 1;
    }
  }
  // Guard against binary-fraction drift (e.g. 5.999999999999999).
  return Math.round(months * 1e6) / 1e6;
}

export interface TenantDayShare {
  contractId: string;
  tenantName: string;
  fromIso: string; // clamped overlap start within the invoice period
  toIso: string; // clamped overlap end within the invoice period
  days: number;
  fraction: number; // days / periodDays
  amount: number; // this tenant's share of the cost
}

export interface VacancyInterval {
  fromIso: string;
  toIso: string;
  days: number;
  amount: number; // tenant-portion of these vacant days → owner
}

export interface OccupancyProration {
  periodDays: number;
  tenantPortion: number; // total cost borne by tenants (before vacancy)
  ownerBase: number; // structural owner share (gedeeld); 0 for huurder
  shares: TenantDayShare[]; // sorted oldest period first
  vacancyDays: number;
  vacancyAmount: number; // tenant-portion of vacant days → owner
  vacancyIntervals: VacancyInterval[]; // the actual gap periods
  ownerTotal: number; // ownerBase + vacancyAmount
  total: number; // == amount
}

// Shift an ISO date (yyyy-mm-dd) by a whole number of days.
function shiftIso(iso: string, days: number): string {
  const ms = parseDate(iso).getTime() + days * MS_PER_DAY;
  return new Date(ms).toISOString().slice(0, 10);
}

export interface Interval {
  from: string;
  to: string;
}

// Money in this engine is exact to the cent. Every split goes through
// splitCents (largest-remainder method, see money.utils) so the parts always
// sum to exactly the amount being split — no cent ever appears or disappears
// through rounding.
export { splitCents };

// Merged day-coverage of a set of contracts within a period: how many days of
// the period are covered by at least one contract, plus the uncovered gaps.
// Overlapping contracts count a day once, so vacancy is never negative.
export function mergedCoverage(
  contracts: ContractRef[],
  period: { from: string; to: string },
): { coveredDays: number; gaps: { from: string; to: string; days: number }[] } {
  const clamped = contracts
    .map(c => ({
      from: c.startDate > period.from ? c.startDate.slice(0, 10) : period.from,
      to: c.endDate < period.to ? c.endDate.slice(0, 10) : period.to,
    }))
    .filter(iv => iv.from <= iv.to)
    .sort((a, b) => a.from.localeCompare(b.from));

  const merged: { from: string; to: string }[] = [];
  for (const iv of clamped) {
    const last = merged[merged.length - 1];
    if (last && iv.from <= shiftIso(last.to, 1)) {
      if (iv.to > last.to) last.to = iv.to;
    } else {
      merged.push({ ...iv });
    }
  }

  const gaps: { from: string; to: string; days: number }[] = [];
  let cursor = period.from;
  for (const iv of merged) {
    if (iv.from > cursor) {
      const gapTo = shiftIso(iv.from, -1);
      gaps.push({ from: cursor, to: gapTo, days: daysInPeriod(cursor, gapTo) });
    }
    const next = shiftIso(iv.to, 1);
    if (next > cursor) cursor = next;
  }
  if (cursor <= period.to) {
    gaps.push({
      from: cursor,
      to: period.to,
      days: daysInPeriod(cursor, period.to),
    });
  }

  const coveredDays = merged.reduce(
    (s, iv) => s + daysInPeriod(iv.from, iv.to),
    0,
  );
  return { coveredDays, gaps };
}

// Provision days per slice of the provision (see ProvisionSlice), as a
// settlement counts them or an earlier one already counted them.
export type SliceIntervals = Partial<Record<ProvisionSlice, Interval[]>>;

// Per contract, the provision days earlier confirmed settlements already
// counted: per slice, or one flat list that holds for every slice (older
// callers, and snapshots that predate the slices).
export type SettledProvisionIntervals = Record<
  string,
  Interval[] | SliceIntervals
>;

// The slices of the provision a contract weighs in this settlement. Without
// slices (older callers, tests) the whole provision is one "common" slice,
// as contractExpectedProvision reads it.
export const contractProvisionSlices = (
  contract: Pick<ContractRef, "provisionSlices">,
): ProvisionSlice[] => contract.provisionSlices ?? ["common"];

// The days of one slice that `settled` holds; a flat list holds for every
// slice.
export const settledIntervalsOfSlice = (
  settled: Interval[] | SliceIntervals | undefined,
  slice: ProvisionSlice,
): Interval[] => {
  if (!settled) return [];
  return Array.isArray(settled) ? settled : settled[slice] ?? [];
};

// Every day of `lists`, as sorted intervals where touching ones are merged.
export function unionIntervals(lists: Interval[][]): Interval[] {
  const sorted = lists
    .flat()
    .map(iv => ({ ...iv }))
    .sort((a, b) => a.from.localeCompare(b.from));
  const merged: Interval[] = [];
  for (const iv of sorted) {
    const last = merged[merged.length - 1];
    if (last && iv.from <= shiftIso(last.to, 1)) {
      if (iv.to > last.to) last.to = iv.to;
    } else {
      merged.push(iv);
    }
  }
  return merged;
}

// Each lease's days within `periods`, as sorted merged intervals.
const leaseDaysWithin = (
  contracts: ContractRef[],
  periods: Interval[],
): Record<string, Interval[]> => {
  const out: Record<string, Interval[]> = {};
  for (const c of contracts) {
    const raw: Interval[] = [];
    for (const period of periods) {
      const from = c.startDate > period.from ? c.startDate : period.from;
      const to = c.endDate < period.to ? c.endDate : period.to;
      if (from > to) continue;
      raw.push({ from, to });
    }
    out[c.id] = unionIntervals([raw]);
  }
  return out;
};

// The provision days each contract counts, per slice it weighs: every day of
// the settlement period in which the lease ran, whichever costs were chosen
// (D7, Lewis 2026-10-16), minus the days an earlier confirmed settlement
// already counted for that slice. A cost of the period that arrives later
// goes in full to the tenant in a supplementary settlement. Each month of
// provision is settled once per slice (Lewis, 2026-10-05; the ledger is
// (slice, lease), 2026-10-16): a building settlement that counted the common
// slice leaves the private slice of the same months open, and the reverse.
// A departure's period runs up to the lease's last day, so the same rule
// holds within it.
export function provisionIntervalsBySlice(
  contracts: ContractRef[],
  settlementPeriod: Interval,
  alreadySettled?: SettledProvisionIntervals,
): Record<string, SliceIntervals> {
  const covered = leaseDaysWithin(contracts, [settlementPeriod]);
  const out: Record<string, SliceIntervals> = {};
  for (const c of contracts) {
    const bySlice: SliceIntervals = {};
    // The days a kind was a forfait count no provision (N71).
    const fixedFeeDays = leaseFixedFeePeriods(c).daysBySlice;
    for (const slice of contractProvisionSlices(c)) {
      bySlice[slice] = subtractIntervals(
        subtractIntervals(covered[c.id] ?? [], fixedFeeDays[slice] ?? []),
        settledIntervalsOfSlice(alreadySettled?.[c.id], slice),
      );
    }
    out[c.id] = bySlice;
  }
  return out;
}

// Every day of any slice in `bySlice`.
export const unionOfSlices = (
  bySlice: SliceIntervals | undefined,
): Interval[] => unionIntervals(Object.values(bySlice ?? {}));

// The provision days each contract counts, of any slice it weighs (see
// provisionIntervalsBySlice).
export function provisionIntervalsByContract(
  contracts: ContractRef[],
  settlementPeriod: Interval,
  alreadySettled?: SettledProvisionIntervals,
): Record<string, Interval[]> {
  const bySlice = provisionIntervalsBySlice(
    contracts,
    settlementPeriod,
    alreadySettled,
  );
  return Object.fromEntries(
    contracts.map(c => [c.id, unionOfSlices(bySlice[c.id])]),
  );
}

// The provision days a settlement confirmed before D7 counted when it froze
// none (the oldest snapshots): the rule of its day, the lease's days within
// the periods of its costs charged to the tenant, or within its own period
// when it settled none. An owner-only cost had nothing to offset (Lewis,
// 2026-10-05). rentio-api-next's getLegacyProvisionDays mirrors it.
export function legacyProvisionIntervalsByContract(
  contracts: ContractRef[],
  settledCosts: Invoice[],
  settlementPeriod: Interval,
): Record<string, Interval[]> {
  const tenantCostPeriods = settledCosts
    .filter(i => i.target !== "owner" && !isOwnerOnlyCategory(i.category))
    .map(i => ({ from: i.periodFrom, to: i.periodTo }));
  return leaseDaysWithin(
    contracts,
    tenantCostPeriods.length > 0 ? tenantCostPeriods : [settlementPeriod],
  );
}

// `intervals` minus every day covered by `remove` (both inclusive ISO days).
export function subtractIntervals(
  intervals: Interval[],
  remove: Interval[],
): Interval[] {
  let rest = intervals.map(iv => ({ ...iv }));
  for (const r of remove) {
    const next: Interval[] = [];
    for (const iv of rest) {
      if (r.to < iv.from || r.from > iv.to) {
        next.push(iv);
        continue;
      }
      if (r.from > iv.from)
        next.push({ from: iv.from, to: shiftIso(r.from, -1) });
      if (r.to < iv.to) next.push({ from: shiftIso(r.to, 1), to: iv.to });
    }
    rest = next;
  }
  return rest;
}

// Each occupant's weight in the occupied days: its own days, where a day it
// shares with other leases of the unit is split between them.
const occupantDayWeights = (params: {
  occupants: { contract: ContractRef }[];
  period: { from: string; to: string };
}): number[] =>
  sharedDayWeights({
    ranges: params.occupants.map(o => ({
      from: o.contract.startDate,
      to: o.contract.endDate,
    })),
    period: params.period,
  });

// Splits one property's cost across the tenants that occupied it during the
// invoice period, pro rata by overlapping days on a yearly basis. Vacant days
// (no active contract) fall on the owner.
// tenantSharePct = 100 for "tenant", the tenant percentage for "shared".
export function prorateByOccupancy(
  amount: number,
  tenantSharePct: number,
  contracts: ContractRef[],
  period: { from: string; to: string },
): OccupancyProration {
  const periodDays = daysInPeriod(period.from, period.to);
  const totalCents = toCents(amount);
  const tenantPortionCents = tenantShareCents({ totalCents, tenantSharePct });
  const ownerBaseCents = totalCents - tenantPortionCents;

  const occupants = uniqueContracts(contracts)
    .map(c => ({
      contract: c,
      days: overlapDays(c.startDate, c.endDate, period.from, period.to),
    }))
    .filter(o => o.days > 0);

  // Vacancy comes from the merged coverage, so overlapping contracts count a
  // day once (they share that day's cost instead of both paying it in full).
  const { coveredDays, gaps } = mergedCoverage(
    occupants.map(o => o.contract),
    period,
  );
  const vacancyDays = periodDays - coveredDays;
  const [vacancyCents] = splitCents(tenantPortionCents, [
    vacancyDays,
    coveredDays,
  ]);
  const occupiedCents = tenantPortionCents - vacancyCents;

  // Per day: a day two leases overlap is shared by both, not weighted by
  // each lease's own day count (which gave a December move-in 92% of
  // December next to a lease that was never ended).
  const shareCents = splitCents(
    occupiedCents,
    occupantDayWeights({ occupants, period }),
  );
  const shares: TenantDayShare[] = occupants.map((o, i) => ({
    contractId: o.contract.id,
    tenantName: o.contract.tenantName,
    fromIso:
      o.contract.startDate > period.from
        ? o.contract.startDate.slice(0, 10)
        : period.from,
    toIso:
      o.contract.endDate < period.to
        ? o.contract.endDate.slice(0, 10)
        : period.to,
    days: o.days,
    fraction: periodDays > 0 ? o.days / periodDays : 0,
    amount: fromCents(shareCents[i]),
  }));

  // Oldest period first.
  shares.sort((a, b) => a.fromIso.localeCompare(b.fromIso));

  const gapCents = splitCents(
    vacancyCents,
    gaps.map(g => g.days),
  );
  const vacancyIntervals: VacancyInterval[] = gaps.map((g, i) => ({
    fromIso: g.from,
    toIso: g.to,
    days: g.days,
    amount: fromCents(gapCents[i]),
  }));

  return {
    periodDays,
    tenantPortion: fromCents(tenantPortionCents),
    ownerBase: fromCents(ownerBaseCents),
    shares,
    vacancyDays,
    vacancyAmount: fromCents(vacancyCents),
    vacancyIntervals,
    ownerTotal: fromCents(ownerBaseCents + vacancyCents),
    total: fromCents(totalCents),
  };
}

export interface BuildingUnitShare {
  unitId: string; // real property id of the unit
  unitLabel: string;
  weight: number; // this unit's apportionment weight
  totalWeight: number; // sum of all unit weights
  unitSlice: number; // amount * weight / totalWeight (tenant + owner, pre-occupancy)
  proration: OccupancyProration; // per-unit split over its tenants
}

// How well the chosen verdeelsleutel actually covers the building. A sleutel
// is a stored list of weights per unit id; units added to the building after
// the sleutel was made simply are not in it. Silently that means € 0 for them
// and a bigger share for the rest, so the split has to be able to say so.
export interface KeyCoverage {
  // Units the sleutel gives no weight — they receive nothing.
  uncoveredUnitIds: string[];
  // The sleutel matched no unit at all (e.g. weights keyed by ids that no
  // longer exist), so the amount was split equally instead. The result no
  // longer represents the sleutel the user picked.
  fellBackToEqual: boolean;
}

export interface BuildingBreakdown {
  units: BuildingUnitShare[];
  periodDays: number;
  total: number;
  coverage: KeyCoverage;
}

// How a sleutel covers the given units. A key that gives none of them any
// weight is split equally — the one rule of the cost popup, the settlement
// and the statement — so the wizard can warn about it just like the popup.
export function keyCoverage(params: {
  unitIds: string[];
  weights: Record<string, number>;
  isEqual?: boolean;
}): KeyCoverage {
  const { unitIds, weights, isEqual } = params;
  if (isEqual) return { uncoveredUnitIds: [], fellBackToEqual: false };
  const covered = unitIds.filter(u => (weights[u] ?? 0) > 0);
  if (covered.length === 0) {
    return { uncoveredUnitIds: [], fellBackToEqual: unitIds.length > 0 };
  }
  return {
    uncoveredUnitIds: unitIds.filter(u => !((weights[u] ?? 0) > 0)),
    fellBackToEqual: false,
  };
}

// Splits a building-level cost across its units via an apportionment key (weight
// per unit), then within each unit pro rata the days each tenant occupied it
// during the period. Vacant days fall on the owner. `weights` are keyed by unit
// id; if they sum to 0 the cost is split equally over the units.
export function buildingBreakdown(params: {
  amount: number;
  tenantSharePct: number;
  units: { id: string; label: string }[];
  contractsByUnit: Record<string, ContractRef[]>;
  weights: Record<string, number>;
  period: { from: string; to: string };
}): BuildingBreakdown {
  const { amount, tenantSharePct, units, contractsByUnit, weights, period } =
    params;
  const periodDays = daysInPeriod(period.from, period.to);

  const coverage = keyCoverage({ unitIds: units.map(u => u.id), weights });
  const useEqual = !units.some(u => (weights[u.id] ?? 0) > 0);
  const unitWeights = units.map(u =>
    useEqual ? 1 : Math.max(0, weights[u.id] ?? 0),
  );
  const totalWeight = unitWeights.reduce((s, w) => s + w, 0);

  // One tenant/owner split of the whole cost, then each part over the units by
  // the key — the way the settlement splits it (allocate for the tenant part,
  // computeResult for the owner's fixed share). Splitting every unit's slice
  // on its own handed each unit's tie cent to its tenants, so three units at
  // 50% billed the tenants € 50,01 of a € 50,00 share.
  const totalCents = toCents(amount);
  const tenantCents = tenantShareCents({ totalCents, tenantSharePct });
  const tenantSliceCents = splitCents(tenantCents, unitWeights);
  const ownerSliceCents = splitCents(totalCents - tenantCents, unitWeights);

  const unitShares: BuildingUnitShare[] = units.map((u, i) => {
    const occupancy = prorateByOccupancy(
      fromCents(tenantSliceCents[i]),
      100,
      contractsByUnit[u.id] ?? [],
      period,
    );
    const ownerBaseCents = ownerSliceCents[i];
    const unitCents = tenantSliceCents[i] + ownerBaseCents;
    const proration: OccupancyProration = {
      ...occupancy,
      ownerBase: fromCents(ownerBaseCents),
      ownerTotal: fromCents(toCents(occupancy.ownerTotal) + ownerBaseCents),
      total: fromCents(unitCents),
    };
    return {
      unitId: u.id,
      unitLabel: u.label,
      weight: unitWeights[i],
      totalWeight,
      unitSlice: fromCents(unitCents),
      proration,
    };
  });

  return { units: unitShares, periodDays, total: round2(amount), coverage };
}

// Meter readings and metering-company amounts are entered per occupant: one
// value per tenant (`contract:<id>`), measured from move-in to move-out, plus
// one per unit for the days it stood empty (`vacant:<unitId>`). Such a value
// already belongs to that tenant, so it is not spread over occupied days
// again. Drafts from before kept one value per unit (keyed by the bare unit
// id); those still split over the unit's tenants by occupied days.
export const occupantKey = {
  contract: (contractId: string) => `contract:${contractId}`,
  vacant: (unitId: string) => `vacant:${unitId}`,
};

export const isPerOccupant = (values: Record<string, number>): boolean =>
  Object.keys(values).some(
    k => k.startsWith("contract:") || k.startsWith("vacant:"),
  );

export interface AllocateInput {
  contracts: ContractRef[];
  invoices: Invoice[]; // already filtered: same category+scope, eligible
  method: DistributionMethod;
  keyWeights: Record<string, number>;
  isEqualKey: boolean;
  consumption: Record<string, number>;
  buildingTotal: number;
  excessMode: "key" | "landlord";
  companyAmounts: Record<string, number>;
  // What the company amounts add up to cover: the whole category, which can
  // span several invoices. Each invoice then takes its proportional part of
  // every amount instead of all of it. Omitted → this call's own invoices.
  companyTotal?: number;
  period: { from: string; to: string };
  // All unit ids of the group, so the key denominator spans occupied AND vacant
  // units — a vacant unit's slice then falls on the owner instead of inflating
  // the occupied tenants. Omitted → denominator = occupied units only.
  unitIds?: string[];
}

export interface AllocateResult {
  perContract: Record<string, number>; // by contractId (splits a unit's slice
  // across its sequential tenants, so nobody is double-counted)
  landlord: number;
  landlordBase: number; // structural owner share (0 for common costs — key covers 100%)
  landlordVacancy: number; // tenant-share of days without contract → owner
  landlordByUnit: Record<string, number>; // owner vacancy amount, by unitId
  // The structural owner part (no weights, consumption excess, unassigned
  // company amounts) spread over the units by the key — or equally — so a
  // group with several owners can give each owner their own share.
  landlordBaseByUnit: Record<string, number>;
  total: number;
  // Of perContract: each lease's part of a consumption excess split by the
  // key (Y14), so a reading's share can say "30 m³ + aandeel overschot".
  excessPerContract: Record<string, number>;
}

function totalAmount(invoices: Invoice[]): number {
  return invoices.reduce((sum, inv) => sum + settlementAmount(inv), 0);
}

// Distributes one (category, scope=gemeen) bucket across contracts.
// Common costs have no structural owner share — the key already covers 100%.
// The only thing that can fall on the owner is vacancy (contract gaps) or
// leftovers from methods that couldn't fully assign the amount.
export function allocate(input: AllocateInput): AllocateResult {
  const totalCents = toCents(totalAmount(input.invoices));
  const perContractCents: Record<string, number> = {};
  let landlordVacancyCents = 0;
  let landlordBaseCents = 0;
  // Owner amount attributable to a specific unit (vacant days of an occupied
  // unit, or the whole slice of a fully vacant unit). Structural leftovers with
  // no unit stay in landlordBase (unattributed → shown building-level).
  const landlordByUnitCents: Record<string, number> = {};
  const addVacancy = (unitId: string, cents: number) => {
    landlordVacancyCents += cents;
    landlordByUnitCents[unitId] = (landlordByUnitCents[unitId] ?? 0) + cents;
  };

  // Contracts grouped by unit, so a unit's slice is split among ITS contracts
  // (sequential tenants) instead of handed to each one in full.
  const contractsByUnit = new Map<string, ContractRef[]>();
  for (const c of input.contracts) {
    const list = contractsByUnit.get(c.unitId) ?? [];
    list.push(c);
    contractsByUnit.set(c.unitId, list);
  }
  const occupiedUnitIds = [...contractsByUnit.keys()];
  const periodDays = daysInPeriod(input.period.from, input.period.to);
  // Every unit of the group, occupied or not: the key denominator, and the
  // units an owner amount without a unit of its own is spread over.
  const allUnitIds = [
    ...new Set([...(input.unitIds ?? []), ...occupiedUnitIds]),
  ];
  const keyWeightOf = (unitId: string) =>
    input.isEqualKey ? 1 : input.keyWeights[unitId] ?? 0;
  // The key's weights over the group's units. A key that gives none of them
  // any weight splits equally: the same rule as the cost popup
  // (buildingBreakdown). Sending such a cost to the owner instead charged the
  // tenants € 0 while the popup showed them an equal share.
  const keySpread = (): number[] => {
    const weights = allUnitIds.map(keyWeightOf);
    return weights.some(w => w > 0) ? weights : allUnitIds.map(() => 1);
  };
  const landlordBaseByUnitCents: Record<string, number> = {};
  const addBase = (cents: number) => {
    if (cents === 0) return;
    landlordBaseCents += cents;
    if (allUnitIds.length === 0) return;
    splitCents(cents, keySpread()).forEach((part, i) => {
      const unitId = allUnitIds[i];
      landlordBaseByUnitCents[unitId] =
        (landlordBaseByUnitCents[unitId] ?? 0) + part;
    });
  };

  // Give a unit's slice to its occupants pro rata by active days; the uncovered
  // (vacant) part of the period falls on the owner. Merged coverage counts a
  // day once even when contracts overlap, so overlapping tenants share that
  // day's cost instead of both paying it in full, and the split is cent-exact.
  const excessPerContractCents: Record<string, number> = {};
  const distributeUnit = (
    unitId: string,
    unitCents: number,
    // Also records each lease's part here (the excess, see
    // excessPerContract).
    alsoInto?: Record<string, number>,
  ) => {
    if (unitCents === 0) return;
    const occupants = (contractsByUnit.get(unitId) ?? [])
      .map(c => ({
        contract: c,
        days: overlapDays(
          c.startDate,
          c.endDate,
          input.period.from,
          input.period.to,
        ),
      }))
      .filter(o => o.days > 0);
    if (occupants.length === 0) {
      addVacancy(unitId, unitCents);
      return;
    }
    const { coveredDays } = mergedCoverage(
      occupants.map(o => o.contract),
      input.period,
    );
    const [vacancyCents] = splitCents(unitCents, [
      periodDays - coveredDays,
      coveredDays,
    ]);
    const parts = splitCents(
      unitCents - vacancyCents,
      occupantDayWeights({ occupants, period: input.period }),
    );
    occupants.forEach((o, i) => {
      perContractCents[o.contract.id] =
        (perContractCents[o.contract.id] ?? 0) + parts[i];
      if (alsoInto) {
        alsoInto[o.contract.id] = (alsoInto[o.contract.id] ?? 0) + parts[i];
      }
    });
    if (vacancyCents > 0) addVacancy(unitId, vacancyCents);
  };

  // Per-occupant input (see occupantKey): every tenant of the settlement and
  // every unit's vacant days, each charged exactly its own value.
  type OccupantRow =
    | { key: string; contractId: string }
    | { key: string; vacantUnitId: string };
  const occupantRows: OccupantRow[] = [
    ...input.contracts.map(c => ({
      key: occupantKey.contract(c.id),
      contractId: c.id,
    })),
    ...allUnitIds.map(unitId => ({
      key: occupantKey.vacant(unitId),
      vacantUnitId: unitId,
    })),
  ];
  const assignOccupant = (row: OccupantRow, cents: number) => {
    if (cents === 0) return;
    if ("contractId" in row) {
      perContractCents[row.contractId] =
        (perContractCents[row.contractId] ?? 0) + cents;
    } else {
      addVacancy(row.vacantUnitId, cents);
    }
  };

  if (input.method === "key") {
    // The apportionment denominator spans ALL of the group's units — occupied
    // AND vacant — so a vacant unit (in the key but with no rental contract in
    // the period) contributes its full slice to the owner, exactly like the old
    // pre-split model, instead of inflating the occupied tenants' shares.
    if (allUnitIds.length > 0) {
      const slices = splitCents(totalCents, keySpread());
      allUnitIds.forEach((unitId, i) => distributeUnit(unitId, slices[i]));
    } else {
      // No unit at all → nothing tied to a tenant; structural owner.
      addBase(totalCents);
    }
  } else if (input.method === "consumption") {
    // No building total means there is nothing to prorate against (the meter
    // readings were never filled in). Charge the owner rather than silently
    // dropping the cost — the amount has to land somewhere.
    if (input.buildingTotal <= 0) {
      addBase(totalCents);
    } else {
      // One split over [per-unit consumption..., excess] keeps the €/unit price
      // exact: each unit pays total × consumption / buildingTotal, and the
      // excess (main meter minus individual meters) is the remainder. Vacant
      // units take part too: their own consumption is vacancy for their owner,
      // never part of the excess the tenants may share.
      const perOccupant = isPerOccupant(input.consumption);
      const consWeights = perOccupant
        ? occupantRows.map(r => input.consumption[r.key] ?? 0)
        : allUnitIds.map(u => input.consumption[u] ?? 0);
      const sumIndividual = consWeights.reduce((s, w) => s + w, 0);
      const excessWeight = Math.max(0, input.buildingTotal - sumIndividual);
      const slices = splitCents(totalCents, [...consWeights, excessWeight]);
      if (perOccupant) {
        occupantRows.forEach((row, i) => assignOccupant(row, slices[i]));
      } else {
        allUnitIds.forEach((unitId, i) => distributeUnit(unitId, slices[i]));
      }
      const excessCents = slices[slices.length - 1] ?? 0;
      if (excessCents > 0) {
        if (input.excessMode === "landlord") {
          addBase(excessCents);
        } else {
          // excessMode "key": the excess (main meter minus individual meters)
          // follows the category's apportionment key, like the option promises.
          // This used contract.quota before — a field that is hardcoded to 1
          // everywhere, so "verdeel volgens quota" silently meant an equal
          // split no matter which verdeelsleutel was chosen. Same denominator
          // rule as the key method: ALL units, so a vacant unit's share of the
          // excess falls to the owner instead of inflating the tenants'.
          if (allUnitIds.length > 0) {
            const keySlices = splitCents(excessCents, keySpread());
            allUnitIds.forEach((unitId, i) =>
              distributeUnit(unitId, keySlices[i], excessPerContractCents),
            );
          } else {
            addBase(excessCents);
          }
        }
      }
    }
  } else if (input.method === "company") {
    // Every occupant the metering company billed, vacant days included (their
    // amount is vacancy for their owner).
    // The amounts cover the whole category; this invoice takes its share of
    // each of them in one cent-exact split, the unassigned rest going to the
    // owner. Booking the amounts in full per invoice charged a category with
    // two invoices twice.
    const perOccupant = isPerOccupant(input.companyAmounts);
    const values = perOccupant
      ? occupantRows.map(r => input.companyAmounts[r.key] ?? 0)
      : allUnitIds.map(u => input.companyAmounts[u] ?? 0);
    const assigned = values.reduce((sum, v) => sum + v, 0);
    const coverTotal = input.companyTotal ?? totalAmount(input.invoices);
    const slices = splitCents(totalCents, [
      ...values,
      Math.max(0, coverTotal - assigned),
    ]);
    if (perOccupant) {
      occupantRows.forEach((row, i) => assignOccupant(row, slices[i]));
    } else {
      allUnitIds.forEach((unitId, i) => distributeUnit(unitId, slices[i]));
    }
    addBase(slices[slices.length - 1] ?? 0);
  }

  const perContract: Record<string, number> = {};
  for (const [id, cents] of Object.entries(perContractCents)) {
    perContract[id] = fromCents(cents);
  }
  const landlordByUnit: Record<string, number> = {};
  for (const [id, cents] of Object.entries(landlordByUnitCents)) {
    landlordByUnit[id] = fromCents(cents);
  }
  const landlordBaseByUnit: Record<string, number> = {};
  for (const [id, cents] of Object.entries(landlordBaseByUnitCents)) {
    landlordBaseByUnit[id] = fromCents(cents);
  }

  return {
    perContract,
    landlord: fromCents(landlordBaseCents + landlordVacancyCents),
    landlordBase: fromCents(landlordBaseCents),
    landlordVacancy: fromCents(landlordVacancyCents),
    landlordByUnit,
    landlordBaseByUnit,
    total: fromCents(totalCents),
    excessPerContract: Object.fromEntries(
      Object.entries(excessPerContractCents).map(([id, cents]) => [
        id,
        fromCents(cents),
      ]),
    ),
  };
}

// A departure settlement's amounts from the metering company (the departing
// tenant's, see occupantKey) as one of the category's costs takes them: each
// amount spread over the category's costs by their size, in whole cents, so
// the tenant's shares of those costs add up to exactly the amount the broker
// entered. Spread per cost on its own, every share was rounded apart and
// three costs could charge a cent more. The cost then covers only its own
// part (companyTotal = settlementAmount of the cost).
export const departureCompanyAmountsOfCost = (params: {
  invoice: Invoice;
  // The category's selected group costs, this one included.
  categoryCosts: Invoice[];
  companyAmounts: Record<string, number>;
}): Record<string, number> => {
  // A fixed order, so a tie cent always goes to the same cost.
  const costs = [...params.categoryCosts].sort((a, b) =>
    a.id.localeCompare(b.id),
  );
  const index = costs.findIndex(cost => cost.id === params.invoice.id);
  if (index < 0) return {};
  const weights = costs.map(cost => toCents(settlementAmount(cost)));
  return Object.fromEntries(
    Object.entries(params.companyAmounts).map(([key, amount]) => [
      key,
      fromCents(splitCents(toCents(amount), weights)[index] ?? 0),
    ]),
  );
};

export interface CommonCostAllocation {
  // The tenant slice by contract id, as allocate() split it.
  perContract: Record<string, number>;
  landlord: number;
  landlordBase: number;
  landlordVacancy: number;
  // The owner's amount per unit: its vacant days, and its structural part
  // (leftovers plus the fixed owner share of a shared or owner cost).
  vacancyByUnit: Record<string, number>;
  baseByUnit: Record<string, number>;
  // The fixed owner share on top of the tenant slice; 0 for a tenant cost.
  structuralOwner: number;
  // Of perContract: each lease's part of a consumption excess by the key.
  excessPerContract: Record<string, number>;
}

// One common (group) cost split over the group: allocate() for its tenant
// slice, then the fixed owner share of a shared or owner cost over the units
// by the same key. computeResult settles every common cost through here, and
// a unit page recomputes a confirmed settlement's split of one cost with it,
// so both arrive at the same cents.
export function allocateCommonCost(params: {
  invoice: Invoice;
  method: DistributionMethod;
  // Absent: no key was chosen, which splits equally.
  key?: Pick<ApportionmentKey, "isEqual" | "weights">;
  consumption: Record<string, number>;
  buildingTotal: number;
  excessMode: "key" | "landlord";
  companyAmounts: Record<string, number>;
  companyTotal?: number;
  // The leases of the units that existed during the cost's period.
  contracts: ContractRef[];
  // Those units: the fixed owner share is spread over them.
  costUnitIds: string[];
  // The key denominator allocate() divides by (see AllocateInput.unitIds).
  allocateUnitIds?: string[];
}): CommonCostAllocation {
  const { invoice, key, costUnitIds } = params;
  const res = allocate({
    contracts: params.contracts,
    invoices: [invoice],
    method: params.method,
    keyWeights: key?.weights ?? {},
    isEqualKey: key ? !!key.isEqual : true,
    consumption: params.consumption,
    buildingTotal: params.buildingTotal,
    excessMode: params.excessMode,
    companyAmounts: params.companyAmounts,
    companyTotal: params.companyTotal,
    period: { from: invoice.periodFrom, to: invoice.periodTo },
    unitIds: params.allocateUnitIds,
  });
  let { landlord, landlordBase } = res;
  const baseByUnit = { ...res.landlordBaseByUnit };

  // allocate() distributes settlementAmount — the tenant slice. For
  // shared/owner-target common costs the remaining structural owner share
  // must still reach the owner card; without this it vanished entirely
  // (the private branch had the same bug and fixed it the same way).
  // The exact remainder in cents: the tenant slice is already cent-exact
  // (settlementAmount), so tenants + owner add up to the invoice.
  const structuralOwner = fromCents(
    toCents(invoice.amountExclVat) - toCents(settlementAmount(invoice)),
  );
  // INFO: any non-zero remainder, not only a positive one: dropping a
  // negative owner part made money disappear from the totals.
  if (structuralOwner !== 0) {
    landlord = round2(landlord + structuralOwner);
    landlordBase = round2(landlordBase + structuralOwner);
    // The fixed owner share follows the same key over the group's units
    // (equal when there is none), so each unit's owner carries their part.
    const weightOf = (u: string) =>
      key ? (key.isEqual ? 1 : key.weights[u] ?? 0) : 1;
    const weights = costUnitIds.map(weightOf);
    const spread = weights.some(w => w > 0)
      ? weights
      : costUnitIds.map(() => 1);
    if (costUnitIds.length > 0) {
      splitCents(toCents(structuralOwner), spread).forEach((cents, i) => {
        const u = costUnitIds[i];
        baseByUnit[u] = round2((baseByUnit[u] ?? 0) + fromCents(cents));
      });
    }
  }

  return {
    perContract: res.perContract,
    landlord,
    landlordBase,
    landlordVacancy: res.landlordVacancy,
    vacancyByUnit: { ...res.landlordByUnit },
    baseByUnit,
    structuralOwner,
    excessPerContract: res.excessPerContract,
  };
}

export interface AllocateByContractResult {
  perContract: Record<string, number>; // by contractId
  landlord: number;
  landlordBase: number; // structural owner share via apportionment key
  landlordVacancy: number; // tenant-share of vacant days → owner
  landlordByUnit: Record<string, number>; // owner amount, by unitId
  total: number;
}

// Privative/unit invoices: each invoice maps to one unit, then its amount is
// split over ALL contracts that occupied that unit during the period, pro rata
// by overlapping days. Vacant days fall on the owner. Keying by contractId (not
// unitId) is what makes sequential tenants on the same property each get their
// own share instead of double-counting.
export function allocatePrivateByContract(
  invoices: Invoice[],
  contracts: ContractRef[],
  period: { from: string; to: string },
): AllocateByContractResult {
  const perContract: Record<string, number> = {};
  let landlord = 0;
  let landlordBase = 0;
  let landlordVacancy = 0;
  let total = 0;
  // A private cost lives on one unit, so its whole owner part (structural +
  // vacancy) is attributable to that unit.
  const landlordByUnit: Record<string, number> = {};
  const contractsByUnit = new Map<string, ContractRef[]>();
  for (const c of contracts) {
    const list = contractsByUnit.get(c.unitId) ?? [];
    list.push(c);
    contractsByUnit.set(c.unitId, list);
  }

  for (const inv of invoices) {
    if (!inv.unitId) continue;
    // Full excl-btw amount enters the settlement — settlementAmount only kept
    // the tenant slice, which zeros out owner-100% costs and hides them from
    // the eigenaar-card.
    const amount = inv.amountExclVat;
    total += amount;
    const unitContracts = contractsByUnit.get(inv.unitId) ?? [];
    const tenantSharePct =
      inv.target === "owner"
        ? 0
        : inv.target === "shared"
          ? inv.tenantSharePct ?? 0
          : 100;
    const proration = prorateByOccupancy(
      amount,
      tenantSharePct,
      unitContracts,
      period,
    );
    for (const share of proration.shares) {
      perContract[share.contractId] =
        (perContract[share.contractId] ?? 0) + share.amount;
    }
    landlord += proration.ownerTotal;
    landlordBase += proration.ownerBase;
    landlordVacancy += proration.vacancyAmount;
    if (proration.ownerTotal !== 0) {
      landlordByUnit[inv.unitId] =
        (landlordByUnit[inv.unitId] ?? 0) + proration.ownerTotal;
    }
  }
  return {
    perContract,
    landlord,
    landlordBase,
    landlordVacancy,
    landlordByUnit,
    total,
  };
}

export interface ComputeInput {
  contracts: ContractRef[];
  invoices: Invoice[];
  keys: ApportionmentKey[];
  draft: SettlementDraftState;
  // All unit ids of the group (group settlements) — see AllocateInput.unitIds.
  unitIds?: string[];
  // Calendar day each archived unit of the group was archived. A group cost
  // splits only over the units that existed during its own period, with their
  // leases (see groupUnits.utils): `unitIds` reaches back to the earliest
  // selected cost, and a unit archived before another cost began must take
  // no share of that one.
  unitArchivedOn?: Record<string, string>;
  // Provision months per contract already settled by an earlier confirmed
  // settlement in the property tree, per slice; a flat list holds for every
  // slice — see provisionIntervalsBySlice.
  settledProvisionIntervals?: SettledProvisionIntervals;
  // Owner of each unit (sorted owner account ids joined; see
  // usePropertyOwners). Units with the same key belong to the same owner(s);
  // the owner's part is then settled per owner instead of for the whole
  // group at once. Units without a known owner share the key "".
  ownerKeyByUnit?: Record<string, string>;
  // A departure settlement: only this lease's share of the costs counts,
  // nothing for the owner or the unit's other tenants.
  departureContractId?: string;
  // Earlier departure settlements of leases in this settlement (see
  // ISettledDeparture).
  departures?: ISettledDeparture[];
  // Per contract, the provision lines an earlier confirmed settlement left
  // out of a slice it counted (decision 14): this settlement counts them for
  // their open days in its period (see reopenedProvisionLines).
  openProvisionLines?: Record<string, IOpenProvisionLine[]>;
  // A settlement of a unit without a building (or a departure from one):
  // its tenants get one balance, no balance per kind (decision 6).
  isStandaloneUnit?: boolean;
  // The costs a confirmed settlement other than a departure already
  // settled, and the departure corrections confirmed settlements made
  // (decision 3; see departureCorrectionOf).
  settledElsewhereInvoiceIds?: string[];
  confirmedCorrections?: IDepartureCorrection[];
  // Every lease's rows in the confirmed settlements of the property tree
  // (settledLeaseRows): the lease end each settled it up to (M01, M07).
  settledRows?: ISettledLeaseRow[];
}

// Every day that more of `plus` than of `minus` covers, as sorted merged
// intervals: what a lease was charged (or counted) per day once the
// corrections that gave days back are taken off, however often its end
// moved back and forth.
export function netIntervals(plus: Interval[], minus: Interval[]): Interval[] {
  const steps = new Map<string, number>();
  const step = (day: string, by: number) =>
    steps.set(day, (steps.get(day) ?? 0) + by);
  for (const iv of plus) {
    if (iv.from > iv.to) continue;
    step(iv.from.slice(0, 10), 1);
    step(shiftIso(iv.to.slice(0, 10), 1), -1);
  }
  for (const iv of minus) {
    if (iv.from > iv.to) continue;
    step(iv.from.slice(0, 10), -1);
    step(shiftIso(iv.to.slice(0, 10), 1), 1);
  }
  const days = [...steps.keys()].sort();
  const out: Interval[] = [];
  let count = 0;
  let openFrom: string | undefined;
  for (const day of days) {
    count += steps.get(day) ?? 0;
    if (count > 0 && openFrom == null) openFrom = day;
    if (count <= 0 && openFrom != null) {
      out.push({ from: openFrom, to: shiftIso(day, -1) });
      openFrom = undefined;
    }
  }
  return unionIntervals([out]);
}

// The days of `intervals` within `window`.
const clipIntervals = (intervals: Interval[], window: Interval): Interval[] =>
  window.from > window.to
    ? []
    : subtractIntervals(intervals, subtractIntervals(intervals, [window]));

const daysOf = (intervals: Interval[]): number =>
  intervals.reduce((sum, iv) => sum + daysInPeriod(iv.from, iv.to), 0);

const lastDayOf = (intervals: Interval[]): string | undefined =>
  intervals.length > 0
    ? intervals.map(iv => iv.to.slice(0, 10)).sort()[intervals.length - 1]
    : undefined;

const FAR_FUTURE = "2999-12-31";

// What correcting one lease whose end moved takes (decision 3; see
// departureCorrection.utils), per kind in whole cents: the costs of the
// days corrected (+ the tenant pays, - they get back), their VAT, and the
// provision (+ paid back, - credited). Undefined when nothing is left to
// correct.
export interface IDepartureCorrectionParts {
  from: string;
  to: string;
  until: string;
  end: string;
  costCentsByKind: Partial<Record<TFixedFeeKindKey, number>>;
  vatCentsByKind: Partial<Record<TFixedFeeKindKey, number>>;
  provisionCentsByKind: Partial<Record<TFixedFeeKindKey, number>>;
  invoiceIds: string[];
  byInvoice: Record<string, IDepartureCorrectionInvoice>;
  provisionCredited: SliceIntervals;
  provisionTakenBack: SliceIntervals;
  provisionCorrected: boolean;
}

// One share a confirmed settlement charged a lease for a cost, per day of
// `days`: what a correction of some of those days takes.
interface IShareRate {
  cents: number;
  vatCents: number;
  days: number;
}

export const leaseEndCorrectionOf = (params: {
  // The lease as it is now, as this settlement weighs it.
  lease: ContractRef;
  // Its departure settlement, if any.
  departure?: ISettledDeparture;
  // The rows of the lease in confirmed settlements (settledLeaseRows).
  rows: ISettledLeaseRow[];
  // The corrections confirmed settlements made of the lease.
  earlier: IDepartureCorrection[];
  invoices: Invoice[];
  // The costs this settlement takes, and those another confirmed
  // settlement already settled.
  selectedIds: Set<string>;
  settledElsewhereIds: Set<string>;
  // This settlement's period: the provision of its months is its own (D7).
  period: Interval;
}): IDepartureCorrectionParts | undefined => {
  const { lease, departure, rows, earlier, invoices, period } = params;
  const end = lease.endDate.slice(0, 10);
  const start = lease.startDate.slice(0, 10);
  const until = departure?.until.slice(0, 10);
  const fixedFeeDays = leaseFixedFeePeriods(lease).daysBySlice;
  const out: IDepartureCorrectionParts = {
    from: "",
    to: "",
    until: until ?? end,
    end,
    costCentsByKind: {},
    vatCentsByKind: {},
    provisionCentsByKind: {},
    invoiceIds: [],
    byInvoice: {},
    provisionCredited: {},
    provisionTakenBack: {},
    provisionCorrected: false,
  };
  const corrected: Interval[] = [];
  let previousEnd: string | undefined;
  const notePrevious = (day: string | undefined) => {
    if (day && (!previousEnd || day > previousEnd)) previousEnd = day;
  };
  const add = (
    target: Partial<Record<TFixedFeeKindKey, number>>,
    kind: TFixedFeeKindKey,
    cents: number,
  ) => {
    if (cents !== 0) target[kind] = (target[kind] ?? 0) + cents;
  };
  const prorate = (rate: IShareRate | undefined, days: number) =>
    rate && rate.days > 0 && days > 0
      ? {
          cents: Math.round((rate.cents * days) / rate.days),
          vatCents: Math.round((rate.vatCents * days) / rate.days),
        }
      : { cents: 0, vatCents: 0 };

  // Costs: per cost, the days charged to the lease so far (its departure,
  // the settlement that settled the cost, the corrections), against the
  // lease's days now.
  const ids = new Set<string>([
    ...Object.keys(departure?.costByInvoice ?? {}),
    ...rows
      .filter(row => !row.isDeparture)
      .flatMap(row => Object.keys(row.costByInvoice)),
    ...earlier.flatMap(correctionInvoiceIds),
  ]);
  for (const invoiceId of ids) {
    const inv = invoices.find(candidate => candidate.id === invoiceId);
    if (!inv) continue;
    const costPeriod = {
      from: inv.periodFrom.slice(0, 10),
      to: inv.periodTo.slice(0, 10),
    };
    const kind = costKindOf(inv) === COMMON_KIND ? COMMON_KIND : PRIVATE_KIND;
    // A forfait's days carry no cost (N71).
    const offFixedFee = (intervals: Interval[]) =>
      subtractIntervals(
        intervals,
        fixedFeeDays[kind === COMMON_KIND ? "common" : "private"] ?? [],
      );
    const within = (window: Interval) =>
      offFixedFee(clipIntervals([costPeriod], window));
    const held = departure?.costByInvoice[invoiceId];
    const heldDays =
      held != null && until ? within({ from: start, to: until }) : [];
    const departureRate: IShareRate | undefined =
      held != null && until
        ? {
            cents: toCents(held),
            vatCents: toCents(departure?.vatByInvoice?.[invoiceId] ?? 0),
            days: daysOf(heldDays),
          }
        : undefined;
    const plus: Interval[] = [...heldDays];
    const minus: Interval[] = [];
    let settlerRate: IShareRate | undefined;
    if (params.selectedIds.has(invoiceId)) {
      // This settlement charges the lease its days after the departure by
      // its own split: only what the departure held after the end now goes
      // back.
      if (!departureRate) continue;
      plus.push(...within({ from: shiftIso(until ?? end, 1), to: end }));
    } else if (params.settledElsewhereIds.has(invoiceId)) {
      const settler = rows.find(
        row => !row.isDeparture && row.invoiceIds.includes(invoiceId),
      );
      const share = settler?.costByInvoice[invoiceId];
      if (settler?.leaseEnd && share != null && toCents(share) !== 0) {
        const days = within({
          from: departureRate && until ? shiftIso(until, 1) : start,
          to: settler.leaseEnd,
        });
        plus.push(...days);
        settlerRate = {
          cents: toCents(share),
          vatCents: toCents(settler.vatByInvoice[invoiceId] ?? 0),
          days: daysOf(days),
        };
      }
      for (const correction of earlier) {
        const done = correctionDaysOf(correction, invoiceId);
        if (!done) continue;
        (done.sign === 1 ? plus : minus).push(...done.days);
      }
    } else {
      // Held by the departure, settled by nobody yet: the settlement that
      // takes it charges the lease up to its end then.
      continue;
    }
    const charged = netIntervals(plus, minus);
    const last = lastDayOf(charged);
    if (!last) continue;
    notePrevious(last);
    const back = clipIntervals(charged, {
      from: shiftIso(end, 1),
      to: FAR_FUTURE,
    });
    const more =
      back.length === 0 && last < end
        ? within({ from: shiftIso(last, 1), to: end })
        : [];
    const window = back.length > 0 ? back : more;
    if (window.length === 0) continue;
    const sign: 1 | -1 = back.length > 0 ? -1 : 1;
    // Days up to the departure's last day at the departure's own share,
    // the days after it at the share of the settlement that settled the
    // cost (or the departure's, when that one charged the lease none).
    const early = until
      ? clipIntervals(window, { from: "0000-01-01", to: until })
      : [];
    const late = until
      ? clipIntervals(window, { from: shiftIso(until, 1), to: FAR_FUTURE })
      : window;
    const a = prorate(departureRate ?? settlerRate, daysOf(early));
    const b = prorate(settlerRate ?? departureRate, daysOf(late));
    const cents = sign * (a.cents + b.cents);
    const vatC = sign * (a.vatCents + b.vatCents);
    if (cents === 0 && vatC === 0) continue;
    add(out.costCentsByKind, kind, cents);
    add(out.vatCentsByKind, kind, vatC);
    out.invoiceIds.push(invoiceId);
    out.byInvoice[invoiceId] = {
      sign,
      days: window,
      amount: fromCents(cents),
      vat: fromCents(vatC),
    };
    corrected.push(...window);
  }

  // Provision: per slice this settlement weighs, the days counted so far
  // (every confirmed row of the lease and the corrections).
  const frozenEnds = new Set<string>([
    ...(until ? [until] : []),
    ...rows.flatMap(row => (row.leaseEnd ? [row.leaseEnd.slice(0, 10)] : [])),
    ...earlier.map(correction => correction.end.slice(0, 10)),
  ]);
  const beforePeriod = shiftIso(period.from, -1);
  for (const slice of contractProvisionSlices(lease)) {
    const counted = netIntervals(
      [
        ...rows.flatMap(row => row.provisionIntervalsBySlice?.[slice] ?? []),
        ...earlier.flatMap(c => c.provisionCreditedBySlice?.[slice] ?? []),
      ],
      earlier.flatMap(c => c.provisionTakenBackBySlice?.[slice] ?? []),
    );
    const last = lastDayOf(counted);
    if (!last) continue;
    notePrevious(last);
    const kind = slice === "common" ? COMMON_KIND : PRIVATE_KIND;
    // Counted for days the lease no longer runs: paid back.
    const back = clipIntervals(counted, {
      from: shiftIso(end, 1),
      to: FAR_FUTURE,
    });
    // The lease runs past the end a settlement counted it up to: the days
    // in between before this settlement's period are credited here (those
    // in it count as usual).
    const more =
      back.length === 0 && frozenEnds.has(last) && last < end
        ? subtractIntervals(
            subtractIntervals(
              clipIntervals([{ from: start, to: end }], {
                from: shiftIso(last, 1),
                to: beforePeriod,
              }),
              counted,
            ),
            fixedFeeDays[slice] ?? [],
          )
        : [];
    const days = back.length > 0 ? back : more;
    if (days.length === 0) continue;
    const lastDay = lastDayOf(days) ?? end;
    const priced = leaseExpectedProvision({
      contract: {
        ...lease,
        endDate: lastDay > end ? lastDay : lease.endDate,
      },
      intervalsBySlice: { [slice]: days },
    });
    const cents = toCents(priced.bySlice?.[slice] ?? 0);
    if (cents === 0) continue;
    if (back.length > 0) {
      add(out.provisionCentsByKind, kind, cents);
      out.provisionTakenBack[slice] = days;
    } else {
      add(out.provisionCentsByKind, kind, -cents);
      out.provisionCredited[slice] = days;
    }
    out.provisionCorrected = true;
    corrected.push(...days);
  }
  if (out.invoiceIds.length === 0 && !out.provisionCorrected) return undefined;
  const span = unionIntervals([corrected]);
  out.from = span[0].from;
  out.to = span[span.length - 1].to;
  out.until = previousEnd ?? out.until;
  return out;
};

// What a departed lease's frozen shares (`held`, per cost, settled up to
// `until`) come to for the days between two lease ends, excl. VAT, in
// cents: what a settlement that charged the lease up to `from` carried for
// the days up to `to`, or the other way round. The note on a confirmed
// settlement whose lease end moved since says it (M01).
export const departedDaysCents = (params: {
  held: Record<string, number>;
  lease: Pick<ContractRef, "startDate">;
  until: string;
  between: [string, string];
  invoices: Invoice[];
}): number => {
  const [a, b] = [...params.between].map(day => day.slice(0, 10)).sort();
  if (a === b) return 0;
  return Object.entries(params.held).reduce((sum, [invoiceId, amount]) => {
    const inv = params.invoices.find(candidate => candidate.id === invoiceId);
    if (!inv) return sum;
    const base = overlapDays(
      params.lease.startDate,
      params.until,
      inv.periodFrom,
      inv.periodTo,
    );
    const days = overlapDays(shiftIso(a, 1), b, inv.periodFrom, inv.periodTo);
    return base > 0 && days > 0
      ? sum + Math.round((toCents(amount) * days) / base)
      : sum;
  }, 0);
};

// Whether a tenant gets one balance instead of a balance per kind (decision
// 6, Lewis 2026-10-20): on a unit without a building every cost is the
// unit's own, so a common provision would only ever come back in full next
// to private costs to pay; and a lease with one provision field (its lines
// all of one kind) has nothing to keep apart. A lease whose lines are not
// known (older callers) keeps its kinds.
export const hasSingleBalance = (params: {
  contract: Pick<ContractRef, "provisionLines">;
  isStandaloneUnit?: boolean;
}): boolean => {
  if (params.isStandaloneUnit) return true;
  const lines = params.contract.provisionLines;
  if (!lines) return false;
  return new Set(lines.map(line => line.slice)).size <= 1;
};

// A provision line an earlier confirmed settlement left out (the broker
// unticked it) while it counted the rest of its slice (decision 14, Lewis,
// 2026-10-20): the line stays open per line and comes back in a later
// settlement. `intervals` are the days still open for it.
export interface IOpenProvisionLine {
  // The line's key on the lease (IProvisionLine.key).
  lineKey: string;
  slice: ProvisionSlice;
  intervals: Interval[];
  // The settlements that left it out.
  settlementNames: string[];
}

// The checkbox of an open line in the provisions step.
export const reopenedLineKey = (lineKey: string): string =>
  `reopened:${lineKey}`;

// One open line this settlement can count: its days within the settlement
// period and the lease, and its part of its slice's provision for those
// days (the slice spread over its lines as provisionLineRows does).
export interface IReopenedLine {
  key: string;
  lineKey: string;
  slice: ProvisionSlice;
  line: IProvisionLine;
  intervals: Interval[];
  months: number;
  amount: number;
  settlementNames: string[];
}

export const reopenedProvisionLines = (params: {
  contract: ContractRef;
  openLines?: IOpenProvisionLine[];
  period: Interval;
}): IReopenedLine[] => {
  const { contract: c, period } = params;
  const slices = contractProvisionSlices(c);
  const lease = {
    from: c.startDate > period.from ? c.startDate : period.from,
    to: c.endDate < period.to ? c.endDate : period.to,
  };
  if (lease.from > lease.to) return [];
  const fixedFeeDays = leaseFixedFeePeriods(c).daysBySlice;
  return (params.openLines ?? []).flatMap(open => {
    if (!slices.includes(open.slice)) return [];
    const line = (c.provisionLines ?? []).find(
      candidate => candidate.key === open.lineKey,
    );
    // A line the lease no longer has, or of another slice now, stays open.
    if (!line || line.slice !== open.slice) return [];
    const inLease = subtractIntervals(
      [lease],
      subtractIntervals([lease], open.intervals),
    );
    const intervals = subtractIntervals(
      unionIntervals([inLease]),
      fixedFeeDays[open.slice] ?? [],
    );
    if (intervals.length === 0) return [];
    const sliceAmount =
      contractExpectedProvision(c, { [open.slice]: intervals }).bySlice[
        open.slice
      ]?.expected ?? 0;
    const row = provisionLineRows({
      slice: open.slice,
      lines: c.provisionLines ?? [],
      computed: sliceAmount,
      expected: sliceAmount,
      sliceTypedIn: false,
    }).find(candidate => candidate.key === open.lineKey);
    if (!row) return [];
    return [
      {
        key: reopenedLineKey(open.lineKey),
        lineKey: open.lineKey,
        slice: open.slice,
        line,
        intervals,
        months: intervals.reduce(
          (sum, iv) => sum + monthsBetween(iv.from, iv.to),
          0,
        ),
        amount: row.expected,
        settlementNames: open.settlementNames,
      },
    ];
  });
};

// What a departure settlement froze for a cost against what this settlement
// computes for the departed lease up to its last day: the owner of the
// lease's unit carries the difference (see ISettledDeparture), a credit when
// the departure charged more. Each is labelled on the owner card and the
// owner's statement ("Verschil met vertreksafrekening <tenant>", F85).
export interface OwnerDepartureDiff {
  settlementId: string;
  contractId: string;
  tenantName: string;
  unitId: string;
  amount: number; // excl. VAT, either sign
  vat: number; // its own VAT, the sign of its amount (F02)
}

// One cost's part for one owner (all units of that owner together).
export interface OwnerCostLine {
  invoiceId: string;
  label: string; // breakdown key, e.g. "Cleaning" or "Water (privé)"
  // Fixed owner share (shared %, key slice, excess, ...). Without the
  // departure differences, which are listed in departureDiffs; lines frozen
  // before those existed hold them here.
  base: number;
  vacancy: number; // the tenant part of days and units without a tenant
  amount: number; // base + vacancy + the departure differences, excl. VAT
  // This cost's VAT for the owner, at its own rate, the departure
  // differences' VAT included.
  vat: number;
  ownerPct?: number; // the cost's fixed owner % when it has one
  // Absent when no departure of the owner's units left a difference.
  departureDiffs?: OwnerDepartureDiff[];
  // A unit's own cost: the unit it is booked on, so a building settlement
  // shows the owner one block per own unit next to the building's part.
  // Absent for a group cost, and on lines frozen before it existed.
  unitId?: string;
  // A correction of a lease end that moved (decision 3): the part of
  // `amount` that is provision, which carries no VAT. Absent otherwise.
  provision?: number;
}

export interface OwnerResult {
  ownerKey: string;
  unitIds: string[];
  total: number; // excl. VAT
  vat: number;
  // The fixed share per category, the departure differences included (as in
  // the result's landlordBaseByCat), so every reader of the totals stays
  // right; departureDiffByCat says how much of it they are.
  baseByCat: Record<string, number>;
  vacancyByCat: Record<string, number>;
  vatByCat: Record<string, number>;
  // Absent when there are none, and on owners frozen before it existed.
  departureDiffByCat?: Record<string, number>;
  lines: OwnerCostLine[];
}

// Whether an owner gets a card and a statement: a part to carry, or a
// departure difference to explain, also when it cancels the rest.
export const ownerHasPart = (
  owner: Pick<OwnerResult, "total"> & { lines?: OwnerCostLine[] },
): boolean =>
  Math.abs(owner.total) > 0.01 ||
  (owner.lines ?? []).some(line => (line.departureDiffs ?? []).length > 0);

// The departure differences of an owner's lines per category, one entry per
// departure settlement (its costs of one category together), in a fixed
// order: what the owner card shows on rows of their own.
export const departureDiffsByCat = (
  lines: OwnerCostLine[] | undefined,
): Record<string, OwnerDepartureDiff[]> => {
  const byCat: Record<string, OwnerDepartureDiff[]> = {};
  for (const line of lines ?? []) {
    for (const diff of line.departureDiffs ?? []) {
      const diffs = (byCat[line.label] ??= []);
      const same = diffs.find(d => d.settlementId === diff.settlementId);
      if (same) {
        same.amount = round2(same.amount + diff.amount);
        same.vat = round2(same.vat + diff.vat);
      } else {
        diffs.push({ ...diff });
      }
    }
  }
  for (const diffs of Object.values(byCat)) {
    diffs.sort(
      (a, b) =>
        a.tenantName.localeCompare(b.tenantName) ||
        a.settlementId.localeCompare(b.settlementId),
    );
  }
  return byCat;
};

export interface ComputeResult {
  perContract: PerContractResult[];
  totCost: number;
  totProvision: number;
  net: number;
  landlordTotal: number;
  // Owner-borne costs per category label (vacancy + structural owner share),
  // keyed the same way as a contract's byCat breakdown. Drives the owner card.
  landlordBreakdown: Record<string, number>;
  // VAT the owner carries per category label, accumulated at each cost's own
  // rate (never a category average).
  landlordVatByCat: Record<string, number>;
  // Same key set, split into structural share (apportionment key) vs vacancy
  // (huurleegstand). base+vacancy = landlordBreakdown per key. The base
  // includes the departure differences (see OwnerDepartureDiff): only the
  // owner lines and the owner card show them apart, so every total read
  // from these maps stays whole.
  landlordBaseByCat: Record<string, number>;
  landlordVacancyByCat: Record<string, number>;
  // Owner apportionment percentage per category (weighted by cost amount).
  // Only defined for keys where the pct is unambiguous across contributing
  // costs; entries with mixed pcts stay absent so the UI can hide the badge.
  landlordPctByCat: Record<string, number>;
  // Categories whose chosen verdeelsleutel could not be resolved any more —
  // it was picked once and deleted since. The split then silently reverts to
  // an equal one, which is a different settlement than the user approved, so
  // the summary has to be able to say which categories are affected.
  missingKeyCategories: string[];
  // The owner part split per owner of the units (one entry when every unit
  // has the same owner, or none is known).
  owners: OwnerResult[];
  // Owner amount attributable to each unit, and per unit per category label —
  // drive the per-pand owner cards in the summary.
  landlordByUnit: Record<string, number>;
  landlordByUnitCat: Record<string, Record<string, number>>;
  // Owner amount not tied to any unit (structural leftovers) — building-level.
  landlordUnattributed: number;
  // The shares earlier departure settlements already settled (see
  // ISettledDeparture), one line per lease and cost.
  alreadySettled: IAlreadySettledLine[];
  // Which provision lines a chosen cost of their kind reaches (see
  // provisionLineInclusion.utils): the provisions step warns under a
  // ticked line that none reaches, as its provision goes back in full.
  provisionCoverage: IProvisionCoverage;
  // Per building cost split by readings or a metering company's amounts,
  // each unit's part, excl. VAT: its tenants', its owner's and what a
  // departure held of it (Y16). Frozen on confirm, so a unit page (and an
  // owner who sees only their own units' figures) shows it without redoing
  // a split it cannot redo. Absent when no such cost is chosen.
  unitSharesByInvoice?: Record<string, Record<string, number>>;
  // The departures whose lease end moved that this settlement corrects
  // (decision 3). Absent when none.
  departureCorrections?: IDepartureCorrection[];
}

// The departures holding a share of a metered (consumption) group cost whose
// departed tenant's reading this settlement leaves empty (no reading above
// 0): their frozen share stands for that reading (Y12). Only a cost the
// tenants carry in full, read per occupant.
export const departuresWithEmptyReading = (params: {
  inv: Invoice;
  departures: ISettledDeparture[];
  draft: SettlementDraftState;
  isDeparture: boolean;
}): ISettledDeparture[] => {
  const { inv, departures, draft, isDeparture } = params;
  if (inv.scope !== "common" || isDeparture) return [];
  if (toCents(settlementAmount(inv)) !== toCents(inv.amountExclVat)) return [];
  const method = resolveDistributionMethod(
    inv.category,
    draft.distributionMethod,
    { isDeparture },
  );
  const consumption = draft.consumption[inv.category] ?? {};
  if (method !== "consumption" || !isPerOccupant(consumption)) return [];
  return departures.filter(
    departure =>
      departure.costByInvoice[inv.id] != null &&
      !((consumption[occupantKey.contract(departure.contractId)] ?? 0) > 0),
  );
};

// The tenant percentage of a cost: all of it, none for an owner cost, or the
// cost's own share when it is shared.
const tenantSharePctOf = (inv: Invoice): number =>
  inv.target === "owner"
    ? 0
    : inv.target === "shared"
      ? inv.tenantSharePct ?? 0
      : 100;

// One selected cost split over the leases of a settlement: each lease's
// share and the owner's part.
interface CostSplit {
  perContract: Record<string, number>;
  landlord: number;
  landlordBase: number;
  landlordVacancy: number;
  // The owner amount the per-pand cards show per unit: all of a unit cost's
  // owner part, the vacancy of a group cost.
  landlordByUnit: Record<string, number>;
  // The owner part per unit, fixed share vs vacancy: what lets each owner of
  // a group get only the part of their own units.
  baseByUnit: Record<string, number>;
  vacancyByUnit: Record<string, number>;
  // The owner percentage the cost adds to its category badge. Unit costs know
  // their own; a group cost (fully key-driven) only when it has a fixed owner
  // share.
  ownerPct: number | null;
  // Of perContract: a consumption excess split by the key (Y14).
  excessPerContract?: Record<string, number>;
}

// Whether a lease takes part in a settlement: it carries a cost or a
// provision, or its tenancy overlaps the settlement period or a settled
// cost's period (a tenant on a forfait or without provision still gets a "no
// balance" row). A lease that starts after all of them, or ended before, is
// no party: no row, no statement, no action. Without known lease dates it
// stays in.
// Whether a tenant's row of a settlement carries an amount: a cost share
// (correction lines included) or a provision. The summary shows a card only
// for these, and the settlement freezes a row, a statement and an action
// only for these (M18).
export const hasTenantAmounts = (result: {
  cost: number;
  provision: number;
}): boolean =>
  Math.abs(result.cost) >= 0.005 || Math.abs(result.provision) >= 0.005;

export const isSettlementParty = (params: {
  result: { cost: number; provision: number };
  lease?: Interval;
  periods: Interval[];
}): boolean => {
  const { result, lease, periods } = params;
  if (Math.abs(result.cost) >= 0.005 || Math.abs(result.provision) >= 0.005) {
    return true;
  }
  if (!lease) return true;
  return periods.some(
    period => overlapDays(lease.from, lease.to, period.from, period.to) > 0,
  );
};

// The VAT the parties of a cost that departures already settled a share of
// must carry together: the VAT of the whole cost minus the VAT those
// departures froze, so tenants, owners and the "already settled" lines add
// up to exactly the invoice's VAT (F34). Taking the VAT
// of what is left instead was a cent off in about a quarter of the cases.
// Undefined (the VAT of the parties' own total) for a cost no departure
// held, and when the frozen VAT is more than a cent per departure away from
// it: a cost whose VAT rate changed after its departure would otherwise
// push the whole difference onto the remaining parties.
const heldCostVatCents = (params: {
  inv: Invoice;
  departures: ISettledDeparture[];
  partyCents: number;
  vatPct: number;
}): number | undefined => {
  const { inv, partyCents, vatPct } = params;
  const held = params.departures.filter(
    departure => departure.costByInvoice[inv.id] != null,
  );
  if (held.length === 0) return undefined;
  const settledCents = held.reduce(
    (sum, departure) => sum + toCents(departure.costByInvoice[inv.id]),
    0,
  );
  const frozenVatCents = held.reduce(
    (sum, departure) => sum + toCents(departure.vatByInvoice?.[inv.id] ?? 0),
    0,
  );
  const totalVatCents =
    vatCents({ amountCents: partyCents + settledCents, vatPct }) -
    frozenVatCents;
  const ownVatCents = vatCents({ amountCents: partyCents, vatPct });
  return Math.abs(totalVatCents - ownVatCents) <= held.length + 1
    ? totalVatCents
    : undefined;
};

// The kinds of a lease a settlement does not settle because they are a
// forfait (see forfaitKindsOf). A forfait is never settled (decision 20,
// 2026-10-20): a provision added by hand changes nothing about it.
// A kind that was a forfait for only part of the lease (a switch inside
// the period, N71) is settled for its other days: see leaseForfaitPeriods.
export const leaseFixedFeeKinds = (params: {
  contract: Pick<
    ContractRef,
    "id" | "fixedFeeCommon" | "fixedFeePrivate" | "startDate" | "endDate"
  > &
    Partial<Pick<ContractRef, "provisionSchedule">>;
  // Kept for the callers; a provision added by hand no longer matters.
  draft?: SettlementDraftState;
}): TFixedFeeKindKey[] => {
  const periods = leaseFixedFeePeriods(params.contract);
  return fixedFeeKindsOf({
    contract: {
      fixedFeeCommon: periods.common,
      fixedFeePrivate: periods.private,
    },
  });
};

// The provisions the broker added by hand for a lease, with an amount: they
// come off the balance on top of the lease's, unless the broker unticked
// them or the contract opted out of all of its provisions. One in a kind
// the lease prices as a forfait never counts (decision 20): that kind is not
// settled at all, and the step does not let the broker choose it.
const addedProvisionsOf = (
  draft: SettlementDraftState,
  contract: ContractRef,
): IExtraProvision[] => {
  const fixedFeeKinds = leaseFixedFeeKinds({ contract });
  return (draft.extraProvisions?.[contract.id] ?? [])
    .filter(
      extra =>
        !fixedFeeKinds.includes(addedProvisionKindOf(extra)) &&
        isProvisionLineIncluded({
          draft,
          contractId: contract.id,
          lineKey: addedProvisionLineKey(extra.id),
        }),
    )
    .map(extra => ({ ...extra, amount: round2(extra.amount) }))
    .filter(extra => Math.abs(extra.amount) >= 0.005);
};

// Whether a lease has an extra cost line (contract.additionalCosts) that is a
// provision. Such a line belongs to the private kind (settlementKinds.utils).
const hasExtraProvisionLine = (contract: ContractRef): boolean =>
  (contract.provisionLines ?? []).some(
    line => line.slice === "private" && line.kind === "additional",
  );

// A lease as a settlement weighs its provision when some of its kinds are a
// forfait: without the slices of those kinds, so no provision of theirs
// counts (a forfait line is no provision line, but a correction billed on
// it could still be read from the history) and the ledger records no month
// of them as settled.
// One exception (decision 3 of 2026-10-20, M33): a private kind that is a
// forfait keeps its slice when the lease has an extra line that is a
// provision ("Tuinonderhoud" € 10 a month). The forfait's own field is no
// provision line, so that slice holds the extra lines only, and with the
// kind's costs going to the owner no cost stands against them: the tenant
// gets them back in full, and the ledger records their months like any
// provision's, so they are never refunded twice.
const withoutFixedFeeSlices = (
  contract: ContractRef,
  fixedFeeKinds: TFixedFeeKindKey[],
): ContractRef => {
  if (!contract.provisionSlices || fixedFeeKinds.length === 0) return contract;
  const fixedFeeSlices = fixedFeeKinds
    .map(sliceOfFixedFeeKind)
    .filter(slice => !(slice === "private" && hasExtraProvisionLine(contract)));
  const provisionSlices = contract.provisionSlices.filter(
    slice => !fixedFeeSlices.includes(slice),
  );
  return provisionSlices.length === contract.provisionSlices.length
    ? contract
    : { ...contract, provisionSlices };
};

// The slice of the provision a cost's kind stands against: the common kind
// the common slice, the private kind (and any other) the private slice.
const sliceOfCostKind = (kind: TSettlementKindKey): ProvisionSlice =>
  isFixedFeeKindKey(kind) ? sliceOfFixedFeeKind(kind) : "private";

// Spec §9.4
export function computeResult(input: ComputeInput): ComputeResult {
  const { draft, invoices, keys, unitIds } = input;
  // A kind the lease prices as a forfait is not settled with that tenant
  // (Lewis, 2026-10-16): no costs, no provision, no balance. Their share of
  // each cost of that kind goes to the owner of their unit, exactly where it
  // would have gone had they not been there, as part of the owner's own share
  // ("Eigenaarsaandeel"); it is never moved to the other tenants.
  const fixedFeeKindsById: Record<string, TFixedFeeKindKey[]> = {};
  // The slices each lease weighed before its forfait kinds were left out:
  // a forfait kind of a slice the settlement weighs is named on the card.
  const weighedSlicesById: Record<string, ProvisionSlice[] | undefined> = {};
  const contracts = uniqueContracts(input.contracts).map(c => {
    const kinds = leaseFixedFeeKinds({ contract: c, draft });
    if (kinds.length > 0) fixedFeeKindsById[c.id] = kinds;
    weighedSlicesById[c.id] = c.provisionSlices;
    return withoutFixedFeeSlices(c, kinds);
  });
  // Per lease, the forfait kinds whose costs it had a share of here.
  const fixedFeeCostKindsById: Record<string, Set<TFixedFeeKindKey>> = {};
  // Per lease, the slices of its provision that a chosen cost of the same
  // kind reaches (see provisionLineInclusion.utils): only for a warning.
  const coveredSlicesById: Record<string, Set<ProvisionSlice>> = {};
  // Whether a chosen cost charges any tenant anything: without one, the
  // provision lines start unticked (linesStartTicked).
  let chargesTenants = false;
  // Categories where a forfait share went to the owner: their owner
  // percentage no longer says what the owner carries, so no badge.
  const fixedFeeLabels = new Set<string>();

  // An ownership cost is billed to the owner even when an older record still
  // names the tenant (see withBilledTarget).
  const selected = invoices
    .filter(i => draft.selectedInvoiceIds[i.id] && settlementEligible(i))
    .map(withBilledTarget);
  // What a metering company's amounts cover: the category's selected group
  // costs together (see AllocateInput.companyTotal).
  const commonTotalByCat: Record<string, number> = {};
  const commonCostsByCat: Record<string, Invoice[]> = {};
  for (const inv of selected) {
    if (inv.scope !== "common") continue;
    commonTotalByCat[inv.category] =
      (commonTotalByCat[inv.category] ?? 0) + settlementAmount(inv);
    (commonCostsByCat[inv.category] ??= []).push(inv);
  }
  const isDeparture = !!input.departureContractId;

  const perContract: Record<
    string,
    {
      total: number;
      byCat: Record<string, number>;
      vat: number;
      vatByCat: Record<string, number>;
      vatByInvoice: Record<string, number>;
      costByInvoice: Record<string, number>;
      // The part of each category, and of each cost, that is a consumption
      // excess (Y14).
      excessByCat: Record<string, number>;
      excessByInvoice: Record<string, number>;
      // The costs and their VAT per kind (see settlementKinds.utils), in
      // whole cents.
      costCentsByKind: Record<string, number>;
      vatCentsByKind: Record<string, number>;
      // Per correction key (decision 3), the part that is provision.
      correctionProvisionByKey: Record<string, number>;
    }
  > = {};
  for (const c of contracts) {
    perContract[c.id] = {
      total: 0,
      byCat: {},
      vat: 0,
      vatByCat: {},
      vatByInvoice: {},
      costByInvoice: {},
      excessByCat: {},
      excessByInvoice: {},
      costCentsByKind: {},
      vatCentsByKind: {},
      correctionProvisionByKey: {},
    };
  }
  const departures = input.departures ?? [];
  const alreadySettled: IAlreadySettledLine[] = [];
  const unitSharesByInvoice: Record<string, Record<string, number>> = {};
  // VAT is accumulated PER COST at that cost's own rate. Deriving it later
  // from a category average silently mis-bills whenever one category holds
  // several rates (e.g. 21% and 6% maintenance invoices).
  const landlordVatByCat: Record<string, number> = {};

  let landlordTotal = 0;
  const landlordByCat: Record<string, number> = {};
  const landlordBaseByCat: Record<string, number> = {};
  const landlordVacancyByCat: Record<string, number> = {};
  // Owner amount attributable to each unit (for the per-pand summary), and per
  // unit per category label. Structural owner share not tied to a unit lands in
  // landlordUnattributed (shown as a building-level owner card).
  const landlordByUnit: Record<string, number> = {};
  const landlordByUnitCat: Record<string, Record<string, number>> = {};
  let landlordUnattributed = 0;
  // Sum of (ownerPct × amount) and total amount per category — divided at the
  // end into a weighted average. If two costs in the same category disagreed
  // the amount is still meaningful, but the % gets hidden by the UI.
  const landlordPctWeighted: Record<string, number> = {};
  const landlordPctBase: Record<string, number> = {};
  const landlordPctSeen: Record<string, Set<number>> = {};

  // Costs are the source of truth: each cost is distributed over its OWN period
  // (day proration + apportionment key). The settlement period is only a filter
  // for choosing costs — it does NOT drive any calculation here.
  const missingKeyCategories = new Set<string>();
  const ownerOf = (unitId?: string) =>
    (unitId && input.ownerKeyByUnit?.[unitId]) || "";
  const groupUnitIds = [
    ...new Set([...(unitIds ?? []), ...contracts.map(c => c.unitId)]),
  ];
  const owners: Record<string, OwnerResult> = {};
  const ownerFor = (ownerKey: string): OwnerResult =>
    (owners[ownerKey] ??= {
      ownerKey,
      unitIds: groupUnitIds.filter(u => ownerOf(u) === ownerKey),
      total: 0,
      vat: 0,
      baseByCat: {},
      vacancyByCat: {},
      vatByCat: {},
      lines: [],
    });

  // A lease that only starts after the settlement period takes no share of
  // a cost that runs past it (decision 1, 2026-10-20): its days of that cost
  // are vacancy for the owner here, never billed before the lease began.
  // The cost stays the source: it is still split over its own period. A
  // departure's period ends with its lease, so it keeps every lease.
  const startsAfterPeriod = (c: Pick<ContractRef, "startDate">): boolean =>
    !isDeparture && c.startDate.slice(0, 10) > draft.period.to;

  // One selected cost split over `leases`: the settlement's own leases, or
  // the same with a departed lease ending on its departure's last day.
  const splitCost = (
    inv: Invoice,
    allLeases: ContractRef[],
    // A building total other than the draft's (see heldByEmptyReading).
    buildingTotalOverride?: number,
  ): CostSplit => {
    const period = { from: inv.periodFrom, to: inv.periodTo };
    const leases = allLeases.filter(c => !startsAfterPeriod(c));
    if (inv.scope === "private") {
      const res = allocatePrivateByContract([inv], leases, period);
      return {
        perContract: res.perContract,
        landlord: res.landlord,
        landlordBase: res.landlordBase,
        landlordVacancy: res.landlordVacancy,
        landlordByUnit: res.landlordByUnit,
        baseByUnit: inv.unitId ? { [inv.unitId]: res.landlordBase } : {},
        vacancyByUnit: inv.unitId ? { [inv.unitId]: res.landlordVacancy } : {},
        ownerPct: 100 - tenantSharePctOf(inv),
      };
    }
    const method = resolveDistributionMethod(
      inv.category,
      draft.distributionMethod,
      { isDeparture },
    );
    const key = keys.find(k => k.id === draft.selectedKeyId[inv.category]);
    // A departure settlement keeps its tenant's amount from the metering
    // company to the cent (see departureCompanyAmountsOfCost).
    const companyAmounts = draft.companyAmounts[inv.category] ?? {};
    const company =
      isDeparture && method === "company"
        ? {
            amounts: departureCompanyAmountsOfCost({
              invoice: inv,
              categoryCosts: commonCostsByCat[inv.category] ?? [],
              companyAmounts,
            }),
            total: settlementAmount(inv),
          }
        : { amounts: companyAmounts, total: commonTotalByCat[inv.category] };
    // The units that existed during this cost's own period, and their
    // leases: the same units the cost popup splits it over.
    const existedForCost = (unitId: string) =>
      existedSince({
        archivedOn: input.unitArchivedOn?.[unitId],
        from: inv.periodFrom,
      });
    const allocation = allocateCommonCost({
      invoice: inv,
      method,
      // No key chosen at all → fall back to an equal split, the same default
      // the Verdeelsleutel step seeds. That step only seeds while it is
      // rendered, and the stappenplan lets the user click straight from
      // "Keuze van kosten" to "Samenvatting" — without this fallback every
      // common cost then landed on the owner while tenants were living there.
      key,
      consumption: draft.consumption[inv.category] ?? {},
      buildingTotal:
        buildingTotalOverride ?? draft.buildingTotal[inv.category] ?? 0,
      excessMode: draft.excessMode[inv.category] ?? "landlord",
      companyAmounts: company.amounts,
      companyTotal: company.total,
      contracts: leases.filter(c => existedForCost(c.unitId)),
      costUnitIds: groupUnitIds.filter(existedForCost),
      allocateUnitIds: unitIds?.filter(existedForCost),
    });
    // allocate() already split each unit's slice across its own contracts
    // (sequential tenants), so map straight by contract id.
    const perContract: Record<string, number> = {};
    for (const c of leases) {
      perContract[c.id] = allocation.perContract[c.id] ?? 0;
    }
    return {
      perContract,
      landlord: allocation.landlord,
      landlordBase: allocation.landlordBase,
      landlordVacancy: allocation.landlordVacancy,
      landlordByUnit: allocation.vacancyByUnit,
      baseByUnit: allocation.baseByUnit,
      vacancyByUnit: { ...allocation.vacancyByUnit },
      ownerPct:
        allocation.structuralOwner !== 0 ? 100 - tenantSharePctOf(inv) : null,
      excessPerContract: allocation.excessPerContract,
    };
  };

  // A departed lease's share of a cost for its days up to its departure's
  // last day: the cost split again with the lease ending that day, every
  // other lease as it is. A lease that ends by then keeps its share; a
  // reading or company amount per tenant does not count days, so it stays
  // whole.
  const departedShareUntil = (params: {
    inv: Invoice;
    departure: ISettledDeparture;
    share: number;
  }): number => {
    const { inv, departure, share } = params;
    const until = departure.until.slice(0, 10);
    const lease = contracts.find(c => c.id === departure.contractId);
    if (!lease || lease.endDate.slice(0, 10) <= until) return share;
    const leases = contracts.map(c =>
      c.id === lease.id ? { ...c, endDate: until } : c,
    );
    return splitCost(inv, leases).perContract[lease.id] ?? 0;
  };

  for (const inv of selected) {
    // Picked once, gone now: falling back to equal would quietly reshape the
    // split, so record it instead of letting it pass.
    const keyId =
      inv.scope === "private" ? undefined : draft.selectedKeyId[inv.category];
    if (keyId && !keys.some(k => k.id === keyId)) {
      missingKeyCategories.add(inv.category);
    }

    // A departed tenant whose departure holds a share of this metered cost
    // and whose reading is left empty (Y12, hertest 19/10/2026): their
    // share is what the departure froze, so it comes off the cost before
    // the readings and the excess are split; it never lands in the excess
    // the others or the owners pay, and never comes back to the owner as a
    // difference. A reading entered for them counts as usual.
    const heldByEmptyReading = departuresWithEmptyReading({
      inv,
      departures,
      draft,
      isDeparture,
    });
    const heldCents = heldByEmptyReading.reduce(
      (sum, departure) => sum + toCents(departure.costByInvoice[inv.id]),
      0,
    );
    // The frozen share stands for the tenant's consumption at the cost's
    // price per unit: the building total shrinks by it too, so the excess
    // holds no consumption of theirs.
    const totalCents = toCents(inv.amountExclVat);
    const split =
      heldCents === 0 || totalCents === 0
        ? splitCost(inv, contracts)
        : splitCost(
            { ...inv, amountExclVat: fromCents(totalCents - heldCents) },
            contracts,
            ((draft.buildingTotal[inv.category] ?? 0) *
              (totalCents - heldCents)) /
              totalCents,
          );
    const perContractAmt = { ...split.perContract };
    let { landlord, landlordBase, landlordVacancy } = split;
    const baseByUnit = { ...split.baseByUnit };
    const vacancyByUnit = { ...split.vacancyByUnit };
    const costLandlordByUnit = { ...split.landlordByUnit };
    let ownerPctForBadge = split.ownerPct;
    const costKind = costKindOf(inv);
    // The leases this cost reaches: its split gives them a share. Read
    // before a departure or a forfait moves that share elsewhere: it is the
    // cost's split that stands against the lease's provision of its kind. A
    // lease without slices (older callers) has one provision against every
    // cost.
    for (const c of contracts) {
      if (Math.abs(split.perContract[c.id] ?? 0) < 0.005) continue;
      chargesTenants = true;
      const reached = c.provisionSlices
        ? [sliceOfCostKind(costKind)]
        : contractProvisionSlices(c);
      const covered = (coveredSlicesById[c.id] ??= new Set());
      reached.forEach(slice => covered.add(slice));
    }
    // A split by days (a unit's own cost, or a key): a lease that is gone
    // left its days to its unit's vacancy.
    const isSplitByDays =
      inv.scope === "private" ||
      resolveDistributionMethod(inv.category, draft.distributionMethod, {
        isDeparture,
      }) === "key";

    const label =
      inv.scope === "private" ? `${inv.category} (privé)` : inv.category;

    // A departed lease's share of a cost its departure settlement held is
    // settled already, up to the departure's last day: that part is not
    // charged again, and what this settlement computes for those days on top
    // of it (another key, another split) falls on the owner of the lease's
    // unit. A lease that runs longer now (its end moved, or it has none any
    // more) pays its days after it here (Lewis, 2026-10-09).
    // Each difference is a part of its own for that owner, with VAT of its
    // own sign (see splitVat): a credit carries a VAT credit.
    const departureDiffs: { departure: ISettledDeparture; amount: number }[] =
      [];
    for (const departure of departures) {
      const settledAmount = departure.costByInvoice[inv.id];
      if (settledAmount == null) continue;
      // The lease end this settlement charges the lease up to (M01).
      const departedLeaseEnd = contracts
        .find(c => c.id === departure.contractId)
        ?.endDate.slice(0, 10);
      const leaseEndPart = departedLeaseEnd
        ? { leaseEnd: departedLeaseEnd }
        : {};
      if (heldByEmptyReading.includes(departure)) {
        alreadySettled.push({
          invoiceId: inv.id,
          label,
          settlementId: departure.settlementId,
          name: departure.name,
          contractId: departure.contractId,
          unitId: departure.unitId,
          tenantName: departure.tenantName,
          until: departure.until,
          amount: round2(settledAmount),
          vat: round2(departure.vatByInvoice?.[inv.id] ?? 0),
          difference: 0,
          ...leaseEndPart,
        });
        continue;
      }
      const computed = perContractAmt[departure.contractId] ?? 0;
      const untilShare = departedShareUntil({
        inv,
        departure,
        share: computed,
      });
      perContractAmt[departure.contractId] = round2(computed - untilShare);
      // A departed lease deleted since: split by days, its days up to the
      // departure fell to its unit's vacancy, and the departure settled
      // them. That part comes off the vacancy, never more than the unit has
      // of this cost; what is left is a difference as above. A split by
      // readings or the metering company holds the lease's part elsewhere
      // (the excess), so it all stays a difference there.
      const leaseIsGone = !contracts.some(c => c.id === departure.contractId);
      const fromVacancy =
        leaseIsGone && isSplitByDays
          ? Math.min(
              Math.max(0, round2(settledAmount)),
              Math.max(0, round2(vacancyByUnit[departure.unitId] ?? 0)),
            )
          : 0;
      if (fromVacancy > 0) {
        landlord = round2(landlord - fromVacancy);
        landlordVacancy = round2(landlordVacancy - fromVacancy);
        vacancyByUnit[departure.unitId] = round2(
          vacancyByUnit[departure.unitId] - fromVacancy,
        );
        if (costLandlordByUnit[departure.unitId] != null) {
          costLandlordByUnit[departure.unitId] = round2(
            costLandlordByUnit[departure.unitId] - fromVacancy,
          );
        }
      }
      const difference = round2(untilShare + fromVacancy - settledAmount);
      if (difference !== 0) {
        landlord = round2(landlord + difference);
        landlordBase = round2(landlordBase + difference);
        departureDiffs.push({ departure, amount: difference });
      }
      alreadySettled.push({
        invoiceId: inv.id,
        label,
        settlementId: departure.settlementId,
        name: departure.name,
        contractId: departure.contractId,
        unitId: departure.unitId,
        tenantName: departure.tenantName,
        until: departure.until,
        amount: round2(settledAmount),
        vat: round2(departure.vatByInvoice?.[inv.id] ?? 0),
        difference,
        ...leaseEndPart,
      });
    }

    // A tenant whose lease prices this cost's kind as a forfait: their share
    // falls on the owner of their unit, in the owner's own share (as a part
    // the owner carries anyway, the way a shared cost's owner part does), so
    // the other tenants pay exactly what they would have, and every cent of
    // the cost still has one party. The cost's owner percentage no longer
    // says what the owner carries.
    for (const c of contracts) {
      if (
        !isFixedFeeKindKey(costKind) ||
        !fixedFeeKindsById[c.id]?.includes(costKind)
      ) {
        continue;
      }
      const share = round2(perContractAmt[c.id] ?? 0);
      if (share === 0) continue;
      perContractAmt[c.id] = 0;
      landlord = round2(landlord + share);
      landlordBase = round2(landlordBase + share);
      baseByUnit[c.unitId] = round2((baseByUnit[c.unitId] ?? 0) + share);
      // The per-unit owner amounts hold all of a unit cost's owner part, of
      // a group cost its vacancy only (see CostSplit.landlordByUnit).
      if (inv.scope === "private") {
        costLandlordByUnit[c.unitId] = round2(
          (costLandlordByUnit[c.unitId] ?? 0) + share,
        );
      }
      (fixedFeeCostKindsById[c.id] ??= new Set()).add(costKind);
      ownerPctForBadge = null;
      fixedFeeLabels.add(label);
    }

    // A lease whose kind was a forfait for part of its days (it switched
    // inside the period, N71): the part of its share for those days goes
    // to the owner of its unit the same way, in whole cents by days.
    if (isFixedFeeKindKey(costKind)) {
      const slice = sliceOfFixedFeeKind(costKind);
      const costPeriod = { from: inv.periodFrom, to: inv.periodTo };
      for (const c of contracts) {
        const fixedFeeDays = leaseFixedFeePeriods(c).daysBySlice[slice];
        if (!fixedFeeDays) continue;
        const shareCents = toCents(perContractAmt[c.id] ?? 0);
        if (shareCents === 0) continue;
        const leaseInCost = {
          from: c.startDate > costPeriod.from ? c.startDate : costPeriod.from,
          to: c.endDate < costPeriod.to ? c.endDate : costPeriod.to,
        };
        if (leaseInCost.from > leaseInCost.to) continue;
        const leaseDays = daysInPeriod(leaseInCost.from, leaseInCost.to);
        const offDays = Math.min(
          leaseDays,
          daysWithin(fixedFeeDays, leaseInCost),
        );
        if (offDays === 0) continue;
        const [offCents] = splitCents(shareCents, [
          offDays,
          leaseDays - offDays,
        ]);
        if (offCents === 0) continue;
        const off = fromCents(offCents);
        perContractAmt[c.id] = fromCents(shareCents - offCents);
        landlord = round2(landlord + off);
        landlordBase = round2(landlordBase + off);
        baseByUnit[c.unitId] = round2((baseByUnit[c.unitId] ?? 0) + off);
        if (inv.scope === "private") {
          costLandlordByUnit[c.unitId] = round2(
            (costLandlordByUnit[c.unitId] ?? 0) + off,
          );
        }
        ownerPctForBadge = null;
        fixedFeeLabels.add(label);
      }
    }

    landlordTotal += landlord;

    landlordByCat[label] = (landlordByCat[label] ?? 0) + landlord;
    landlordBaseByCat[label] = (landlordBaseByCat[label] ?? 0) + landlordBase;
    landlordVacancyByCat[label] =
      (landlordVacancyByCat[label] ?? 0) + landlordVacancy;
    for (const [uid, amt] of Object.entries(costLandlordByUnit)) {
      landlordByUnit[uid] = (landlordByUnit[uid] ?? 0) + amt;
      const catMap = (landlordByUnitCat[uid] ??= {});
      catMap[label] = (catMap[label] ?? 0) + amt;
    }
    landlordUnattributed +=
      landlord - Object.values(costLandlordByUnit).reduce((s, a) => s + a, 0);
    if (ownerPctForBadge != null) {
      // Weight by the full excl-btw amount so target="owner" (settlementAmount
      // returns 0 there) still contributes its 100% to the category badge.
      const invAmt = inv.amountExclVat;
      landlordPctWeighted[label] =
        (landlordPctWeighted[label] ?? 0) + ownerPctForBadge * invAmt;
      landlordPctBase[label] = (landlordPctBase[label] ?? 0) + invAmt;
      const set = landlordPctSeen[label] ?? new Set<number>();
      set.add(Math.round(ownerPctForBadge * 100));
      landlordPctSeen[label] = set;
    }
    // One VAT figure per party per cost for everything (summary, payment,
    // confirmed view, statement PDF, cost popup). The VAT of what this cost
    // charges is taken once and split in whole cents over the parties, each
    // tenant and each unit's owner part, by their amounts (see
    // costVat.utils), so together they pass on exactly the invoice's VAT. An
    // owner carries the VAT of their own units' parts. Rounding it per party
    // drifted a cent or two away from the supplier's VAT; rounding over a
    // category or a total made payment and PDF disagree.
    const vatPct = vatPercent(inv.vatPct);
    // Per owner: their units' fixed share and vacancy, and the same per unit
    // for the VAT, and the departure differences of their units. Whatever no
    // unit holds (a group without units) goes to the owner key "".
    const perOwner: Record<
      string,
      { base: number; vacancy: number; departureDiff: number }
    > = {};
    const ownerPartOf = (ownerKey: string) =>
      (perOwner[ownerKey] ??= { base: 0, vacancy: 0, departureDiff: 0 });
    const ownerAmountByUnit: Record<string, number> = {};
    const addTo = (unitId: string, kind: "base" | "vacancy", amt: number) => {
      ownerPartOf(ownerOf(unitId))[kind] += amt;
      ownerAmountByUnit[unitId] = (ownerAmountByUnit[unitId] ?? 0) + amt;
    };
    for (const [u, amt] of Object.entries(baseByUnit)) {
      addTo(u, "base", amt);
    }
    for (const [u, amt] of Object.entries(vacancyByUnit)) {
      addTo(u, "vacancy", amt);
    }
    for (const diff of departureDiffs) {
      ownerPartOf(ownerOf(diff.departure.unitId)).departureDiff += diff.amount;
    }
    const attributed = Object.values(perOwner).reduce(
      (sum, o) => sum + o.base + o.vacancy + o.departureDiff,
      0,
    );
    if (Math.abs(landlord - attributed) >= 0.005) {
      addTo("", "base", landlord - attributed);
    }
    const departureDiffParty = (diff: { departure: ISettledDeparture }) => ({
      kind: "owner" as const,
      partyId: `departure:${diff.departure.settlementId}`,
    });
    const vatParties = [
      ...contracts.map(c => ({
        kind: "tenant" as const,
        partyId: c.id,
        amount: round2(perContractAmt[c.id] ?? 0),
      })),
      ...Object.entries(ownerAmountByUnit).map(([unitId, amt]) => ({
        kind: "owner" as const,
        partyId: unitId,
        amount: round2(amt),
      })),
      ...departureDiffs.map(diff => ({
        ...departureDiffParty(diff),
        amount: diff.amount,
      })),
    ];
    const vatByParty = splitVatOverParties({
      parties: vatParties,
      vatPct,
      totalVatCents: heldCostVatCents({
        inv,
        departures,
        partyCents: vatParties.reduce(
          (sum, party) => sum + toCents(party.amount),
          0,
        ),
        vatPct,
      }),
    });
    const vatByOwner: Record<string, number> = {};
    for (const unitId of Object.keys(ownerAmountByUnit)) {
      const ownerKey = ownerOf(unitId);
      vatByOwner[ownerKey] =
        (vatByOwner[ownerKey] ?? 0) +
        (vatByParty[costPartyKey({ kind: "owner", partyId: unitId })] ?? 0);
    }
    const diffsByOwner: Record<string, OwnerDepartureDiff[]> = {};
    for (const diff of departureDiffs) {
      const ownerKey = ownerOf(diff.departure.unitId);
      const vat = round2(
        vatByParty[costPartyKey(departureDiffParty(diff))] ?? 0,
      );
      vatByOwner[ownerKey] = (vatByOwner[ownerKey] ?? 0) + vat;
      (diffsByOwner[ownerKey] ??= []).push({
        settlementId: diff.departure.settlementId,
        contractId: diff.departure.contractId,
        tenantName: diff.departure.tenantName,
        unitId: diff.departure.unitId,
        amount: diff.amount,
        vat,
      });
    }
    const ownerParts = Object.entries(perOwner)
      .map(([ownerKey, part]) => {
        const base = round2(part.base);
        const vacancy = round2(part.vacancy);
        const diffs = diffsByOwner[ownerKey] ?? [];
        const diffTotal = round2(part.departureDiff);
        const vat = round2(vatByOwner[ownerKey] ?? 0);
        return {
          ownerKey,
          base,
          vacancy,
          diffs,
          diffTotal,
          amount: round2(base + vacancy + diffTotal),
          vat,
        };
      })
      // An owner whose only part is a departure difference, or whose
      // difference cancels the rest, keeps their line.
      .filter(
        part =>
          Math.abs(part.amount) >= 0.005 ||
          part.vat !== 0 ||
          part.diffs.length > 0,
      );
    let costOwnerVat = 0;
    ownerParts.forEach(
      ({ ownerKey, base, vacancy, diffs, diffTotal, amount, vat }) => {
        costOwnerVat += vat;
        const owner = ownerFor(ownerKey);
        owner.total += amount;
        owner.vat += vat;
        owner.baseByCat[label] =
          (owner.baseByCat[label] ?? 0) + base + diffTotal;
        owner.vacancyByCat[label] = (owner.vacancyByCat[label] ?? 0) + vacancy;
        owner.vatByCat[label] = (owner.vatByCat[label] ?? 0) + vat;
        if (diffs.length > 0) {
          const byCat = (owner.departureDiffByCat ??= {});
          byCat[label] = (byCat[label] ?? 0) + diffTotal;
        }
        owner.lines.push({
          invoiceId: inv.id,
          label,
          base,
          vacancy,
          amount,
          vat,
          ownerPct: ownerPctForBadge ?? undefined,
          ...(diffs.length > 0 ? { departureDiffs: diffs } : {}),
          ...(inv.scope === "private" && inv.unitId
            ? { unitId: String(inv.unitId) }
            : {}),
        });
      },
    );
    landlordVatByCat[label] = (landlordVatByCat[label] ?? 0) + costOwnerVat;
    // Each unit's part of a building cost split by readings or a metering
    // company (Y16): what its tenants, its owner and its departed tenants
    // carry of it, in whole cents.
    const costMethod =
      inv.scope === "common"
        ? resolveDistributionMethod(inv.category, draft.distributionMethod, {
            isDeparture,
          })
        : "key";
    if (costMethod !== "key") {
      const unitCents: Record<string, number> = {};
      const add = (unitId: string, amount: number) => {
        if (!unitId) return;
        unitCents[unitId] = (unitCents[unitId] ?? 0) + toCents(amount);
      };
      contracts.forEach(c => add(c.unitId, perContractAmt[c.id] ?? 0));
      Object.entries(ownerAmountByUnit).forEach(([unitId, amount]) =>
        add(unitId, amount),
      );
      departureDiffs.forEach(diff => add(diff.departure.unitId, diff.amount));
      departures.forEach(departure => {
        const held = departure.costByInvoice[inv.id];
        if (held != null) add(departure.unitId, held);
      });
      unitSharesByInvoice[inv.id] = Object.fromEntries(
        Object.entries(unitCents).map(([unitId, cents]) => [
          unitId,
          fromCents(cents),
        ]),
      );
    }
    contracts.forEach(c => {
      const amt = perContractAmt[c.id] ?? 0;
      const vat =
        vatByParty[costPartyKey({ kind: "tenant", partyId: c.id })] ?? 0;
      perContract[c.id].total += amt;
      perContract[c.id].vat += vat;
      const { costCentsByKind, vatCentsByKind } = perContract[c.id];
      if (toCents(amt) !== 0) {
        costCentsByKind[costKind] =
          (costCentsByKind[costKind] ?? 0) + toCents(amt);
      }
      if (toCents(vat) !== 0) {
        vatCentsByKind[costKind] =
          (vatCentsByKind[costKind] ?? 0) + toCents(vat);
      }
      perContract[c.id].byCat[label] =
        (perContract[c.id].byCat[label] ?? 0) + amt;
      perContract[c.id].vatByCat[label] =
        (perContract[c.id].vatByCat[label] ?? 0) + vat;
      if (Math.abs(amt) >= 0.005) {
        perContract[c.id].vatByInvoice[inv.id] = vat;
        perContract[c.id].costByInvoice[inv.id] = round2(amt);
      }
      // The excess part, while the lease carries its share as split.
      const excess = split.excessPerContract?.[c.id] ?? 0;
      if (
        excess !== 0 &&
        toCents(amt) === toCents(split.perContract[c.id] ?? 0)
      ) {
        perContract[c.id].excessByCat[label] = round2(
          (perContract[c.id].excessByCat[label] ?? 0) + excess,
        );
        perContract[c.id].excessByInvoice[inv.id] = round2(excess);
      }
    });
  }

  // A lease whose end moved since a confirmed settlement settled it up to
  // its old end (decision 3): one correction line per kind, for the tenant
  // and, the other way round, for the owner of their unit (see
  // departureCorrection.utils).
  const departureCorrections: IDepartureCorrection[] = [];
  if (!isDeparture) {
    const selectedIds = new Set(selected.map(inv => inv.id));
    const settledElsewhereIds = new Set(input.settledElsewhereInvoiceIds);
    for (const lease of contracts) {
      if (!perContract[lease.id]) continue;
      const leaseDeparture = departures.find(
        candidate => candidate.contractId === lease.id,
      );
      const rows = (input.settledRows ?? []).filter(
        row => row.contractId === lease.id,
      );
      if (!leaseDeparture && rows.length === 0) continue;
      const parts = leaseEndCorrectionOf({
        lease,
        departure: leaseDeparture,
        rows,
        earlier: (input.confirmedCorrections ?? []).filter(
          c => c.contractId === lease.id,
        ),
        invoices,
        selectedIds,
        settledElsewhereIds,
        period: draft.period,
      });
      if (!parts) continue;
      const departure = {
        settlementId:
          leaseDeparture?.settlementId ?? leaseCorrectionId(lease.id),
        contractId: lease.id,
        unitId: leaseDeparture?.unitId ?? String(lease.unitId),
        tenantName: leaseDeparture?.tenantName ?? lease.tenantName,
      };
      const row = perContract[lease.id];
      const owner = ownerFor(ownerOf(departure.unitId));
      let amountCents = 0;
      let vatCentsTotal = 0;
      let provisionCents = 0;
      for (const kind of [COMMON_KIND, PRIVATE_KIND] as TFixedFeeKindKey[]) {
        const costCents = parts.costCentsByKind[kind] ?? 0;
        const provCents = parts.provisionCentsByKind[kind] ?? 0;
        const vatC = parts.vatCentsByKind[kind] ?? 0;
        const cents = costCents + provCents;
        if (cents === 0 && vatC === 0) continue;
        amountCents += cents;
        vatCentsTotal += vatC;
        provisionCents += provCents;
        // A correction line stands against the provision of its kind, as a
        // chosen cost of that kind does (M27).
        (coveredSlicesById[lease.id] ??= new Set()).add(
          kind === PRIVATE_KIND ? "private" : "common",
        );
        const key = departureCorrectionKey({
          settlementId: departure.settlementId,
          from: parts.from,
          to: parts.to,
          isPrivate: kind === PRIVATE_KIND,
        });
        const amount = fromCents(cents);
        const vat = fromCents(vatC);
        row.total += amount;
        row.vat += vat;
        row.byCat[key] = (row.byCat[key] ?? 0) + amount;
        row.vatByCat[key] = (row.vatByCat[key] ?? 0) + vat;
        if (provCents !== 0) {
          row.correctionProvisionByKey[key] = fromCents(
            toCents(row.correctionProvisionByKey[key] ?? 0) + provCents,
          );
        }
        row.costCentsByKind[kind] = (row.costCentsByKind[kind] ?? 0) + cents;
        if (vatC !== 0) {
          row.vatCentsByKind[kind] = (row.vatCentsByKind[kind] ?? 0) + vatC;
        }
        // The owner carries it the other way round.
        owner.total -= amount;
        owner.vat -= vat;
        owner.baseByCat[key] = (owner.baseByCat[key] ?? 0) - amount;
        owner.vatByCat[key] = (owner.vatByCat[key] ?? 0) - vat;
        owner.lines.push({
          invoiceId: key,
          label: key,
          base: -amount,
          vacancy: 0,
          amount: -amount,
          vat: -vat,
          ...(kind === PRIVATE_KIND ? { unitId: departure.unitId } : {}),
          ...(provCents !== 0 ? { provision: -fromCents(provCents) } : {}),
        });
        landlordTotal -= amount;
        landlordByCat[key] = (landlordByCat[key] ?? 0) - amount;
        landlordBaseByCat[key] = (landlordBaseByCat[key] ?? 0) - amount;
        landlordVatByCat[key] = (landlordVatByCat[key] ?? 0) - vat;
        landlordByUnit[departure.unitId] =
          (landlordByUnit[departure.unitId] ?? 0) - amount;
        const catMap = (landlordByUnitCat[departure.unitId] ??= {});
        catMap[key] = (catMap[key] ?? 0) - amount;
      }
      departureCorrections.push({
        settlementId: departure.settlementId,
        contractId: departure.contractId,
        unitId: departure.unitId,
        tenantName: departure.tenantName,
        until: parts.until,
        end: parts.end,
        from: parts.from,
        to: parts.to,
        amount: fromCents(amountCents),
        vat: fromCents(vatCentsTotal),
        provision: fromCents(provisionCents),
        invoiceIds: parts.invoiceIds,
        provisionCorrected: parts.provisionCorrected,
        byInvoice: parts.byInvoice,
        ...(Object.keys(parts.provisionCredited).length > 0
          ? { provisionCreditedBySlice: parts.provisionCredited }
          : {}),
        ...(Object.keys(parts.provisionTakenBack).length > 0
          ? { provisionTakenBackBySlice: parts.provisionTakenBack }
          : {}),
      });
    }
  }

  // A lease with one balance (decision 6, hasSingleBalance) nets every
  // provision against every cost: once a chosen cost reaches it, each of its
  // lines has a cost against it (M27).
  for (const c of contracts) {
    const covered = coveredSlicesById[c.id];
    if (
      covered &&
      covered.size > 0 &&
      hasSingleBalance({
        contract: c,
        isStandaloneUnit: input.isStandaloneUnit,
      })
    ) {
      contractProvisionSlices(c).forEach(slice => covered.add(slice));
    }
  }

  // Which provision lines a chosen cost of their kind reaches (Lewis,
  // 2026-10-17): every line counts, a line none reaches only gets a warning.
  // With costs that charge no tenant anything, the lines start unticked
  // (Lewis, 2026-10-19).
  const provisionCoverage: IProvisionCoverage = {
    coveredSlicesByContract: Object.fromEntries(
      Object.entries(coveredSlicesById).map(([contractId, slices]) => [
        contractId,
        [...slices],
      ]),
    ),
    hasCosts: selected.length > 0,
    chargesTenants,
  };
  const startsTicked = linesStartTicked(provisionCoverage);

  // Expected provision per contract = monthly provision × the lease's months
  // in the settlement period (D7) — the same figure the Provisies step shows.
  // The settlement reconciles the actual cost against THIS (what the tenant
  // was expected to pay), not against what was collected.
  const provisionIntervals = provisionIntervalsBySlice(
    contracts,
    draft.period,
    input.settledProvisionIntervals,
  );
  // Read three times per lease below: computed once.
  const leaseProvisions = new Map<ContractRef, ILeaseProvision>();
  const leaseProvisionFor = (c: ContractRef): ILeaseProvision => {
    const known = leaseProvisions.get(c);
    if (known) return known;
    const lease = leaseExpectedProvision({
      contract: c,
      intervalsBySlice: provisionIntervals[c.id] ?? {},
      override: draft.provisionExpectedOverride?.[c.id],
      overrideBySlice: draft.provisionExpectedOverrideBySlice?.[c.id],
    });
    leaseProvisions.set(c, lease);
    return lease;
  };
  // What of it counts once the broker unticked some of its lines (see
  // includedLeaseProvision); also read three times per lease.
  const includedProvisions = new Map<ContractRef, IIncludedLeaseProvision>();
  const includedProvisionFor = (c: ContractRef): IIncludedLeaseProvision => {
    const known = includedProvisions.get(c);
    if (known) return known;
    const included = includedLeaseProvision({
      contract: c,
      lease: leaseProvisionFor(c),
      draft,
      startsTicked,
    });
    includedProvisions.set(c, included);
    return included;
  };
  const expectedProvisionFor = (c: ContractRef): number =>
    includedProvisionFor(c).expected;
  // The lines an earlier settlement left out that count here (decision
  // 14): ticked unless the broker unticked them, none for a lease that
  // opted out.
  const reopenedFor = (c: ContractRef): IReopenedLine[] =>
    isLeaseOptedOut(draft, c.id)
      ? []
      : reopenedProvisionLines({
          contract: c,
          openLines: input.openProvisionLines?.[c.id],
          period: draft.period,
        }).filter(
          line =>
            Math.abs(line.amount) >= 0.005 &&
            isProvisionLineIncluded({
              draft,
              contractId: c.id,
              lineKey: line.key,
              startsTicked,
            }),
        );
  // The provisions the broker added by hand (see addedProvisionsOf).
  const extraProvisionsFor = (c: ContractRef): IExtraProvision[] =>
    addedProvisionsOf(draft, c);
  // The forfait kinds a tenant's card and statement name: those of a slice
  // the settlement weighs, and any whose costs it had a share of here.
  const shownFixedFeeKindsFor = (c: ContractRef): TFixedFeeKindKey[] => {
    const weighed = weighedSlicesById[c.id];
    return (fixedFeeKindsById[c.id] ?? []).filter(
      kind =>
        fixedFeeCostKindsById[c.id]?.has(kind) ||
        (weighed ?? []).includes(sliceOfFixedFeeKind(kind)),
    );
  };
  // The days of the settlement period a weighed kind of the lease was a
  // forfait while its other days are settled (N71): the card and the
  // provisions step name them. Undefined when there are none.
  const fixedFeeDaysInPeriodFor = (
    c: ContractRef,
  ): SliceIntervals | undefined => {
    const { daysBySlice } = leaseFixedFeePeriods(c);
    const out: SliceIntervals = {};
    for (const slice of contractProvisionSlices(c)) {
      const days = subtractIntervals(
        daysBySlice[slice] ?? [],
        subtractIntervals(daysBySlice[slice] ?? [], [draft.period]),
      );
      if (days.length > 0) out[slice] = days;
    }
    return Object.keys(out).length > 0 ? out : undefined;
  };
  // Provision billed for days outside the lease: shown for information,
  // never part of the balance (no refund flow).
  const billedOutsideContractFor = (c: ContractRef): number =>
    includedProvisionFor(c).slices.length === 0
      ? 0
      : leaseProvisionFor(c).calc.billedOutsideContract;
  // The ledger record of a lease (see PerContractResult): the days each
  // slice counted, and the lease's own provision per slice. An opted-out
  // contract counted none, and a slice whose lines the broker all unticked
  // counted no day (see includedLeaseProvision).
  const provisionLedgerFor = (
    c: ContractRef,
  ): Pick<
    PerContractResult,
    | "provisionIntervals"
    | "provisionIntervalsBySlice"
    | "provisionBySlice"
    | "provisionMonthsBySlice"
    | "provisionLinesLeftOut"
    | "provisionLineIntervals"
  > => {
    const included = includedProvisionFor(c);
    // The days counted for an open line alone, and the lines left out of a
    // slice that counts its days: both kept per line (decision 14).
    const reopened = reopenedFor(c);
    const lineLedger = {
      ...(reopened.length > 0
        ? {
            provisionLineIntervals: Object.fromEntries(
              reopened.map(line => [line.lineKey, line.intervals]),
            ),
          }
        : {}),
      ...(Object.keys(included.leftOutBySlice ?? {}).length > 0
        ? { provisionLinesLeftOut: included.leftOutBySlice }
        : {}),
    };
    if (included.slices.length === 0) {
      return {
        provisionIntervals: [],
        provisionIntervalsBySlice: {},
        ...lineLedger,
      };
    }
    const bySlice: SliceIntervals = Object.fromEntries(
      Object.entries(provisionIntervals[c.id] ?? {}).filter(([slice]) =>
        included.slices.includes(slice as ProvisionSlice),
      ),
    );
    return {
      ...lineLedger,
      provisionIntervals: unionOfSlices(bySlice),
      provisionIntervalsBySlice: bySlice,
      ...(included.bySlice ? { provisionBySlice: included.bySlice } : {}),
      provisionMonthsBySlice: Object.fromEntries(
        Object.entries(bySlice).map(([slice, intervals]) => [
          slice,
          intervals.reduce((sum, iv) => sum + monthsBetween(iv.from, iv.to), 0),
        ]),
      ),
    };
  };

  // The tenant's balance per kind (Lewis, 2026-10-16; settlementKinds.utils):
  // each cost in its kind, and each provision in its kind, line by line as
  // the provisions step shows them (provisionLineRows), the ones added by
  // hand included. Undefined for a lease without slices (older callers), and
  // for one amount typed in for the whole lease of a lease that weighs two
  // slices: that amount cannot be told apart per kind.
  const kindsFor = (params: {
    c: ContractRef;
    extraProvisions: IExtraProvision[];
    monthsBySlice: Partial<Record<ProvisionSlice, number>>;
    cost: number;
    costVat: number;
    provision: number;
  }): ISettlementKind[] | undefined => {
    const { c, extraProvisions, monthsBySlice } = params;
    if (!c.provisionSlices) return undefined;
    if (
      hasSingleBalance({
        contract: c,
        isStandaloneUnit: input.isStandaloneUnit,
      })
    ) {
      return undefined;
    }
    const provisions: {
      kind: TSettlementKindKey;
      provision: ISettlementKindProvision;
    }[] = [];
    const lineTitles: Record<string, string> = {};
    const included = includedProvisionFor(c);
    if (included.slices.length > 0) {
      if (
        !included.bySlice &&
        contractProvisionSlices(c).length > 1 &&
        included.expected !== 0
      ) {
        return undefined;
      }
      for (const slice of included.slices) {
        for (const row of included.rowsBySlice[slice] ?? []) {
          if (Math.abs(row.expected) < 0.005) continue;
          const kind = provisionLineKindOf({
            slice,
            lineKey: row.line?.key,
            lineKind: row.line?.kind,
          });
          const title = row.line?.description;
          if (title && isExtraKind(kind)) lineTitles[kind] = title;
          const months = monthsBySlice[slice];
          provisions.push({
            kind,
            provision: {
              source: "lease",
              key: row.key,
              slice,
              ...(row.line ? { lineKind: row.line.kind } : {}),
              ...(title ? { title } : {}),
              amount: row.expected,
              ...(months != null ? { months } : {}),
            },
          });
        }
      }
    }
    for (const line of reopenedFor(c)) {
      const kind = provisionLineKindOf({
        slice: line.slice,
        lineKey: line.lineKey,
        lineKind: line.line.kind,
      });
      const title = line.line.description;
      if (title && isExtraKind(kind)) lineTitles[kind] = title;
      provisions.push({
        kind,
        provision: {
          source: "lease",
          key: line.key,
          slice: line.slice,
          lineKind: line.line.kind,
          ...(title ? { title } : {}),
          amount: line.amount,
          months: line.months,
        },
      });
    }
    for (const extra of extraProvisions) {
      const title = extra.title.trim();
      provisions.push({
        kind: addedProvisionKindOf(extra),
        provision: {
          source: "added",
          key: extra.id,
          ...(title ? { title } : {}),
          amount: extra.amount,
        },
      });
    }
    const kinds = buildKinds({
      costCentsByKind: perContract[c.id].costCentsByKind,
      vatCentsByKind: perContract[c.id].vatCentsByKind,
      provisions,
      titleOf: key => lineTitles[key],
    });
    return kindsAddUp({
      kinds,
      cost: params.cost,
      costVat: params.costVat,
      provision: params.provision,
    })
      ? kinds
      : undefined;
  };

  const allResults: PerContractResult[] = contracts.map(c => {
    const tot = perContract[c.id].total;
    const extraProvisions = extraProvisionsFor(c);
    const provision = fromCents(
      toCents(expectedProvisionFor(c)) +
        reopenedFor(c).reduce((sum, line) => sum + toCents(line.amount), 0) +
        extraProvisions.reduce((sum, extra) => sum + toCents(extra.amount), 0),
    );
    const billedOutsideContract = billedOutsideContractFor(c);
    const ledger = provisionLedgerFor(c);
    const kinds = kindsFor({
      c,
      extraProvisions,
      monthsBySlice: ledger.provisionMonthsBySlice ?? {},
      cost: round2(tot),
      costVat: round2(perContract[c.id].vat),
      provision,
    });
    const fixedFeeKinds = shownFixedFeeKindsFor(c);
    const fixedFeeDays = fixedFeeDaysInPeriodFor(c);
    const { excessByCat, excessByInvoice, correctionProvisionByKey } =
      perContract[c.id];
    return {
      ...(Object.keys(correctionProvisionByKey).length > 0
        ? { correctionProvisionByKey }
        : {}),
      ...(fixedFeeDays ? { forfaitIntervalsBySlice: fixedFeeDays } : {}),
      ...(Object.keys(excessByCat).length > 0
        ? { excessByCat, excessByInvoice }
        : {}),
      ...(billedOutsideContract > 0
        ? { provisionBilledOutsideContract: billedOutsideContract }
        : {}),
      ...(fixedFeeKinds.length > 0 ? { fixedFeeKinds } : {}),
      ...(extraProvisions.length > 0 ? { extraProvisions } : {}),
      contractId: c.id,
      unitId: c.unitId,
      unitCode: c.unitCode,
      tenantName: c.tenantName,
      cost: round2(tot),
      costVat: round2(perContract[c.id].vat),
      provision,
      balance: round2(tot - provision),
      breakdown: roundMap(perContract[c.id].byCat),
      breakdownVat: roundMap(perContract[c.id].vatByCat),
      vatByInvoice: roundMap(perContract[c.id].vatByInvoice),
      costByInvoice: perContract[c.id].costByInvoice,
      // The lease end it settles the lease up to (a departure: its
      // snapshot.departure.until), so a later settlement corrects the days
      // between it and a lease end that moved (decision 3).
      ...(input.departureContractId
        ? {}
        : { leaseEnd: c.endDate.slice(0, 10) }),
      // An opted-out contract used no provision months.
      ...ledger,
      ...(kinds ? { kinds } : {}),
    };
  });
  // Only the leases that take part (see isSettlementParty): a tenant who
  // moved in after the period got a "no balance" row, a statement and an
  // action of € 0. With costs that charge no tenant anything (owner costs
  // only), a tenant takes part only with a provision the broker ticked
  // (Lewis, 2026-10-19): nothing to settle is no card, no statement and no
  // action. A departure keeps its lease.
  const settledPeriods: Interval[] =
    startsTicked || isDeparture
      ? [
          ...(draft.period ? [draft.period] : []),
          ...selected.map(inv => ({ from: inv.periodFrom, to: inv.periodTo })),
        ]
      : [];
  // A departure settlement settles its one lease, and nothing for the owner:
  // the rest of every cost stays for the settlement of the unit or group.
  const { departureContractId } = input;
  // And a lease with nothing to settle here takes no part either (M18,
  // 2026-10-20): no cost share and no provision, as a departed tenant whose
  // departure settled their share, or a tenant whose kinds are all a fixed
  // amount or settled before. It got an empty statement of € 0, an action
  // of € 0 and a row in the payment status. A departure keeps its own lease,
  // also when it settles € 0 (Y06).
  const parties = allResults.filter(
    (result, i) =>
      !startsAfterPeriod(contracts[i]) &&
      isSettlementParty({
        result,
        lease: { from: contracts[i].startDate, to: contracts[i].endDate },
        periods: settledPeriods,
      }) &&
      (result.contractId === departureContractId || hasTenantAmounts(result)),
  );
  const results = departureContractId
    ? parties.filter(result => result.contractId === departureContractId)
    : parties;

  const totCost = round2(results.reduce((s, r) => s + r.cost, 0));
  const totProvision = round2(results.reduce((s, r) => s + r.provision, 0));

  const landlordPctByCat: Record<string, number> = {};
  for (const label of Object.keys(landlordPctBase)) {
    if (fixedFeeLabels.has(label)) continue;
    const set = landlordPctSeen[label];
    // Ambiguous pct (multiple different values in the same category) → skip.
    if (set && set.size > 1) continue;
    const base = landlordPctBase[label];
    if (base <= 0) continue;
    landlordPctByCat[label] = round2(landlordPctWeighted[label] / base);
  }

  if (departureContractId) {
    return {
      perContract: results,
      totCost,
      totProvision,
      net: round2(totCost - totProvision),
      landlordTotal: 0,
      landlordBreakdown: {},
      landlordVatByCat: {},
      landlordBaseByCat: {},
      landlordVacancyByCat: {},
      landlordPctByCat: {},
      missingKeyCategories: [...missingKeyCategories],
      owners: [],
      landlordByUnit: {},
      landlordByUnitCat: {},
      landlordUnattributed: 0,
      alreadySettled,
      provisionCoverage,
    };
  }

  return {
    perContract: results,
    totCost,
    totProvision,
    net: round2(totCost - totProvision),
    landlordTotal: round2(landlordTotal),
    landlordBreakdown: roundMap(landlordByCat),
    landlordVatByCat: roundMap(landlordVatByCat),
    landlordBaseByCat: roundMap(landlordBaseByCat),
    landlordVacancyByCat: roundMap(landlordVacancyByCat),
    landlordPctByCat,
    missingKeyCategories: [...missingKeyCategories],
    owners: Object.values(owners)
      .map(o => ({
        ...o,
        total: round2(o.total),
        vat: round2(o.vat),
        baseByCat: roundMap(o.baseByCat),
        vacancyByCat: roundMap(o.vacancyByCat),
        vatByCat: roundMap(o.vatByCat),
        ...(o.departureDiffByCat
          ? { departureDiffByCat: roundMap(o.departureDiffByCat) }
          : {}),
      }))
      .sort((a, b) => a.ownerKey.localeCompare(b.ownerKey)),
    landlordByUnit: roundMap(landlordByUnit),
    landlordByUnitCat: Object.fromEntries(
      Object.entries(landlordByUnitCat).map(([uid, m]) => [uid, roundMap(m)]),
    ),
    landlordUnattributed: round2(landlordUnattributed),
    alreadySettled,
    provisionCoverage,
    ...(Object.keys(unitSharesByInvoice).length > 0
      ? { unitSharesByInvoice }
      : {}),
    ...(departureCorrections.length > 0 ? { departureCorrections } : {}),
  };
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function roundMap(m: Record<string, number>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const k of Object.keys(m)) out[k] = round2(m[k]);
  return out;
}

// Expected provision of one contract over its settled intervals, from the
// provision history when the contract carries one (see provisionHistory.ts).
// Without slices (older callers, tests) the contract's full monthly provision
// counts for every month — the history-less estimate. `intervals` is one
// list for every slice, or the days per slice (provisionIntervalsBySlice).
export function contractExpectedProvision(
  c: ContractRef,
  intervals: Interval[] | SliceIntervals,
): ExpectedProvision {
  const slices = contractProvisionSlices(c);
  const intervalsBySlice = Array.isArray(intervals)
    ? undefined
    : Object.fromEntries(slices.map(slice => [slice, intervals[slice] ?? []]));
  const current: Record<ProvisionSlice, number> = c.provisionSlices
    ? {
        common: c.monthlyProvisionCommon ?? 0,
        private: c.monthlyProvisionPrivate ?? 0,
      }
    : { common: c.monthlyProvision ?? 0, private: 0 };
  return expectedProvision({
    intervals: Array.isArray(intervals) ? intervals : unionOfSlices(intervals),
    intervalsBySlice,
    slices,
    current,
    schedule: c.provisionSlices ? c.provisionSchedule : undefined,
    unchanged: c.provisionSlices ? c.provisionUnchanged : undefined,
    indexed: c.provisionSlices ? c.provisionIndexed : undefined,
    contract: { from: c.startDate, to: c.endDate },
  });
}

// A lease's own expected provision in a settlement (without the provisions
// added by hand), and per slice. An amount the broker typed in for a slice
// replaces that slice; the one typed in for the whole lease (drafts made
// before the slices) replaces the total, and then there are no slice parts.
export interface ILeaseProvision {
  expected: number;
  bySlice?: Partial<Record<ProvisionSlice, number>>;
  // What the provision history gives, before any amount typed in.
  calc: ExpectedProvision;
  // The broker typed in an amount, for the lease or for one of its slices.
  isTypedIn: boolean;
}

export const leaseExpectedProvision = (params: {
  contract: ContractRef;
  intervalsBySlice: SliceIntervals;
  override?: number;
  overrideBySlice?: Partial<Record<ProvisionSlice, number>>;
}): ILeaseProvision => {
  const { contract, intervalsBySlice, override, overrideBySlice } = params;
  const calc = contractExpectedProvision(contract, intervalsBySlice);
  const slices = contractProvisionSlices(contract);
  const hasSliceOverride = slices.some(
    slice => overrideBySlice?.[slice] != null,
  );
  if (!hasSliceOverride && override != null) {
    return { expected: round2(override), calc, isTypedIn: true };
  }
  const bySlice: Partial<Record<ProvisionSlice, number>> = {};
  for (const slice of slices) {
    const typed = overrideBySlice?.[slice];
    const computed = calc.bySlice[slice]?.expected;
    if (typed != null) bySlice[slice] = round2(typed);
    else if (computed != null) bySlice[slice] = computed;
  }
  if (!hasSliceOverride) {
    return { expected: calc.expected, bySlice, calc, isTypedIn: false };
  }
  return {
    expected: fromCents(
      Object.values(bySlice).reduce((sum, part) => sum + toCents(part), 0),
    ),
    bySlice,
    calc,
    isTypedIn: true,
  };
};

// What of a lease's own provision counts once the broker unticked some of
// its lines in the provisions step (provisionLineInclusion.utils). Per slice,
// the rows of its lines as the step shows them (provisionLineRows) and the
// ones still ticked.
export interface IIncludedLeaseProvision {
  // The lease's own provision that counts, without the ones added by hand.
  expected: number;
  // Per slice that keeps a line ticked, its ticked lines together; undefined
  // when the broker typed in one amount for the whole lease.
  bySlice?: Partial<Record<ProvisionSlice, number>>;
  // Per slice, the rows of its lines that count.
  rowsBySlice: Partial<Record<ProvisionSlice, IProvisionLineRow[]>>;
  // The slices whose months this settlement counts and records in the
  // ledger: none when the lease opted out.
  slices: ProvisionSlice[];
  // Per counted slice, the lines of the lease the broker unticked: they
  // stay open per line (decision 14). Absent when there are none.
  leftOutBySlice?: Partial<Record<ProvisionSlice, string[]>>;
}

// A slice whose lines are all unticked counts nothing and records no month,
// so a later settlement can still settle them. A slice that keeps one line
// ticked records its months at the amount of its ticked lines, and its
// unticked lines as left out (leftOutBySlice): the ledger keeps them open
// per line, and a later settlement counts them for those months (decision
// 14, Lewis 2026-10-20; reopenedProvisionLines).
export const includedLeaseProvision = (params: {
  contract: ContractRef;
  lease: ILeaseProvision;
  draft: SettlementDraftState;
  // How a line without the broker's choice starts (linesStartTicked).
  startsTicked?: boolean;
}): IIncludedLeaseProvision => {
  const { contract: c, lease, draft, startsTicked } = params;
  if (isLeaseOptedOut(draft, c.id)) {
    return { expected: 0, rowsBySlice: {}, slices: [] };
  }
  const isOn = (lineKey: string) =>
    isProvisionLineIncluded({ draft, contractId: c.id, lineKey, startsTicked });
  const slices = contractProvisionSlices(c);
  // One amount typed in for the whole lease: one row, one checkbox. A lease
  // of one slice still lists its lines in its kinds, as before.
  if (!lease.bySlice) {
    if (!isOn(WHOLE_LEASE_KEY)) {
      return { expected: 0, rowsBySlice: {}, slices: [] };
    }
    const rowsBySlice: IIncludedLeaseProvision["rowsBySlice"] =
      slices.length === 1
        ? {
            [slices[0]]: provisionLineRows({
              slice: slices[0],
              lines: c.provisionLines ?? [],
              computed: lease.calc.bySlice[slices[0]]?.expected ?? 0,
              expected: lease.expected,
              sliceTypedIn: false,
              typedByLine: draft.provisionExpectedOverrideByLine?.[c.id],
            }),
          }
        : {};
    return { expected: lease.expected, rowsBySlice, slices };
  }
  const bySlice: Partial<Record<ProvisionSlice, number>> = {};
  const rowsBySlice: IIncludedLeaseProvision["rowsBySlice"] = {};
  const counted: ProvisionSlice[] = [];
  const leftOutBySlice: Partial<Record<ProvisionSlice, string[]>> = {};
  let anyOff = false;
  for (const slice of slices) {
    const rows = provisionLineRows({
      slice,
      lines: c.provisionLines ?? [],
      computed: lease.calc.bySlice[slice]?.expected ?? 0,
      expected: lease.bySlice[slice] ?? 0,
      sliceTypedIn:
        draft.provisionExpectedOverrideBySlice?.[c.id]?.[slice] != null,
      typedByLine: draft.provisionExpectedOverrideByLine?.[c.id],
    });
    const ticked = rows.filter(row => isOn(row.key));
    if (ticked.length < rows.length) anyOff = true;
    if (ticked.length === 0) continue;
    counted.push(slice);
    rowsBySlice[slice] = ticked;
    const leftOut = rows
      .filter(row => row.line && !isOn(row.key))
      .map(row => row.key);
    if (leftOut.length > 0) leftOutBySlice[slice] = leftOut;
    const sliceAmount = lease.bySlice[slice];
    if (sliceAmount == null) continue;
    bySlice[slice] =
      ticked.length === rows.length
        ? sliceAmount
        : fromCents(
            ticked.reduce((sum, row) => sum + toCents(row.expected), 0),
          );
  }
  return {
    // Every line ticked: exactly the lease's own figure, as before.
    expected: anyOff
      ? fromCents(
          Object.values(bySlice).reduce((sum, part) => sum + toCents(part), 0),
        )
      : lease.expected,
    bySlice,
    rowsBySlice,
    slices: counted,
    ...(Object.keys(leftOutBySlice).length > 0 ? { leftOutBySlice } : {}),
  };
};
